import { useSummaryGeneration } from '../hooks/useSummaryGeneration';
import { isFrontendFeatureEnabled } from '../utils/frontendFeatures';
import { createSummaryChangeScroll } from '../utils/summaryChangeScroll';
import { readSummaryEditorPosition, writeSummaryEditorPosition, summaryPositionSelection } from '../utils/summaryEditorPosition';
import PageHeader from './PageHeader';
import { createSummaryDraftStore, SummaryDraftConflictError } from '../utils/summaryDraftStore';
import { createSummaryStreamEdit, summarySnapshotReview } from '../utils/summaryEditorUpdates';
import { SummaryReview, summaryReviewKey, summaryReviewGroups, reviewTransaction, emptySummaryReview } from '../utils/summaryEditorReview';
import { undoDepth, redoDepth } from '@tiptap/pm/history';
import { DOMSerializer } from '@tiptap/pm/model';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { SummarySaveQueue } from '../utils/summarySaveQueue';
import { mergeSummaryDraft } from '../utils/summaryDraftMerge';
import { useDelayedStatus } from '../hooks/useDelayedStatus';
import { normalizeSummaryMarkdown } from '../utils/normalizeSummaryMarkdown';
import { flushSync } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSummaryUpdatePreferences, SummaryUpdateVersion } from '../utils/summaryUpdatePreferences';
import { SummaryPreviewScheduler, SummaryUpdateMeasurement, withSummaryUpdateLock } from '../utils/summaryUpdateSession';
import { SummaryUpdateInterruptedError, SummaryUpdateError } from '../services/summaryUpdateStream';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { SummaryTable } from './SummaryTable';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { createDocument, Extension, Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import '../styles/SummaryEditor.css';
import TurndownService from 'turndown';
import { Summary, Recording, ChatMessage, SmartTemplateField } from '../types';
import ApiService from '../services/apiService';
import AudioPlayer, { AudioPlayerRef } from './AudioPlayer';
import TranscriptViewer from './TranscriptViewer';
import ShareDialog from './ShareDialog';
import ChatOverlay from './ChatOverlay';
import SummaryChangesReview from './SummaryChangesReview';
import SummaryDraftConflict from './SummaryDraftConflict';
import { SummaryGeneration, setSummaryGenerationStreaming } from './SummaryGeneration';
import SmartFieldOverlayCanvas from './SmartFieldOverlayCanvas';
import { collapseListItemBlankLines } from '../utils/markdownToHtml';
import { FieldAssignment, composeStructuredFieldMarkdown, hasStructuredFieldMarkers, normalizeAssignments, parseStructuredFieldMarkdown } from '../utils/smartFieldMarkers';
import { adjustAssignmentsForContentChange } from '../utils/smartFieldOverlayUtils';
import {
    buildColWidthComment,
    extractColWidthTokensByTable,
    hydrateColumnWidths as hydrateColumnWidthsInEditor,
    isColWidthComment,
    measureColWidthTokens,
} from '../utils/tableColumnWidths';
import { logger } from '../utils/logger';

interface HistoryEntry {
    content: string;
    timestamp: string;
    type: 'initial' | 'user_edit' | 'ai_improvement';
    description?: string;
}

interface SummaryEditorProps {
    summary: Summary;
    recording: Recording;
    meetingTypeTitle?: string;
    onBack: () => void;
    onBackToRecordings?: () => void;
    onUpdate: (summary: Summary) => void;
    onDelete?: (summary: Summary) => void;
    onGenerationReload?: (summaries: Summary[] | null, recording: Recording | null, title: string) => void;
    onRecordingTitleUpdate?: (newTitle: string) => void;
    additionalRecordings?: Recording[];
    canEdit?: boolean;
    canDownload?: boolean;
}

const getSummarySourceContent = (summary: Summary): string => summary.sourceContent ?? summary.content ?? '';

// Functie om markdown content te normaliseren voor consistente diff vergelijking
const normalizeMarkdownContent = (content: string): string => {
    if (!content) return '';

    const visibleContent = parseStructuredFieldMarkdown(content).cleanContent;

    const normalized = visibleContent
        // Normaliseer lijstitems: zowel - als * naar *
        .replace(/^(\s*)- /gm, '$1* ')
        // Normaliseer spatie na lijstitems
        .replace(/^(\s*\* )(\S)/gm, '$1 $2')
        // Normaliseer line endings
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        // Verwijder trailing whitespace
        .replace(/ +$/gm, '')
        // Normaliseer meerdere lege regels naar enkele lege regel
        .replace(/\n{3,}/g, '\n\n');

    // Verwijder blanco regels tussen lijstitems (strakke lijstweergave)
    return collapseListItemBlankLines(normalized)
        // Zorg ervoor dat content eindigt met één newline
        .replace(/\n*$/, '\n')
        .trim();
};

// Vergelijk twee markdown-bronnen op inhoud in plaats van op opmaakdetails.
// De server en Turndown serialiseren bullets, escaping en witruimte
// verschillend, waardoor een strikte stringvergelijking de echo van onze eigen
// opslag niet als "ongewijzigd" herkent. Veldmarkers blijven wél meedoen, zodat
// een verplaatste marker een echte wijziging blijft.
const normalizeSourceForComparison = (content: string): string => (
    (content || '')
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map((line) => line
            .replace(/^(\s*)\\?[-*+](\s)/, '$1*$2')
            .replace(/\\([[\]#*_`|])/g, '$1')
            .replace(/\s+$/, ''))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
);

const isSameSourceContent = (a: string, b: string): boolean => (
    normalizeSourceForComparison(a) === normalizeSourceForComparison(b)
);

// ProseMirror plaatst de selectie na een volledige document-replace aan het
// einde van het document. Staat de gebruiker op dat moment in de editor, dan
// springt de caret dus onderaan het verslag; daarom zetten we de bestaande
// selectie terug.
const setEditorContentPreservingSelection = (editor: any, html: string) => {
    if (!editor) return;
    const position = { anchor: editor.state.selection.anchor, head: editor.state.selection.head };
    const scrollers = [editor.view.dom.closest('main'), editor.view.dom.closest('.editor-content'), document.scrollingElement]
        .filter((element): element is HTMLElement => Boolean(element))
        .map(element => ({ element, top: element.scrollTop }));
    editor.chain().setContent(html, false).setMeta('addToHistory', false).run();
    editor.view.dispatch(editor.state.tr.setSelection(summaryPositionSelection(editor.state.doc, position)).setMeta('addToHistory', false));
    scrollers.forEach(({ element, top }) => { element.scrollTop = top; });
};

const normalizeStreamFeedbackMessage = (message: string): string => (
    message
        .replace(/\s+/g, ' ')
        .trim()
);

const isEditableField = (element: HTMLElement | null): boolean => {
    if (!element) return false;

    const tagName = element.tagName;
    if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
        return true;
    }

    if (element.isContentEditable) {
        return true;
    }

    return Boolean(element.closest('[contenteditable="true"]'));
};

const isSummaryEditorField = (element: HTMLElement | null): boolean => {
    if (!element) return false;

    return Boolean(element.closest('.notion-editor') || element.closest('.ProseMirror'));
};

const applyDiffMarks = (editor: any, oldText: string, newText: string, markdownToHtml: (markdown: string) => string) => {
    if (editor) editor.view.dispatch(reviewTransaction(editor.state.tr, {
        restore: summarySnapshotReview(createDocument(markdownToHtml(oldText), editor.schema), editor.state.doc),
    }));
};

// Custom Mark extension voor AI wijzigingen
const AiChange = Mark.create({
    name: 'aiChange',

    addOptions() {
        return {
            HTMLAttributes: {},
        }
    },

    addAttributes() {
        return {
            type: {
                default: 'modified',
                parseHTML: element => element.getAttribute('data-change-type'),
                renderHTML: attributes => {
                    if (!attributes.type) {
                        return {}
                    }
                    return {
                        'data-change-type': attributes.type,
                    }
                },
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'mark[data-change-type]',
            },
            {
                tag: 'mark.ai-change',
            },
        ]
    },

    renderHTML({ HTMLAttributes }) {
        return ['mark', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
            class: 'ai-change bg-green-100 rounded',
            style: 'background-color: #dcfce7; border-radius: 2px; padding: 0; margin: 0;'
        }), 0]
    },
});

// Houdt de "context selectie" zichtbaar terwijl de editor de focus kwijt is
// aan de prompt-bar: de browser-selectie verdwijnt dan, maar de feedback gaat
// nog steeds over dat tekstfragment. Decoraties zitten niet in het document,
// dus dit raakt de opgeslagen markdown niet.
const selectionContextPluginKey = new PluginKey<DecorationSet>('selectionContextHighlight');

const SelectionContextHighlight = Extension.create({
    name: 'selectionContextHighlight',

    addProseMirrorPlugins() {
        return [
            new Plugin<DecorationSet>({
                key: selectionContextPluginKey,
                state: {
                    init: () => DecorationSet.empty,
                    apply(tr, decorations) {
                        const meta = tr.getMeta(selectionContextPluginKey);
                        if (meta) {
                            if (meta.clear) {
                                return DecorationSet.empty;
                            }
                            return DecorationSet.create(tr.doc, [
                                Decoration.inline(meta.from, meta.to, { class: 'selection-context-highlight' }),
                            ]);
                        }
                        return decorations.map(tr.mapping, tr.doc);
                    },
                },
                props: {
                    decorations(state) {
                        return selectionContextPluginKey.getState(state);
                    },
                },
            }),
        ];
    },
});

const SummaryEditor: React.FC<SummaryEditorProps> = ({
    summary,
    recording,
    meetingTypeTitle = summary.meetingType,
    onBack,
    onBackToRecordings = onBack,
    onUpdate,
    onDelete,
    onRecordingTitleUpdate,
    onGenerationReload,
    additionalRecordings = [],
    canEdit = true,
    canDownload = true,
}) => {
    const { t } = useTranslation();
    const { user, getDomain } = useAuth();
    const [reviewBlocked, setReviewBlocked] = useState(false);
    const [, refreshEditorUI] = useState(0);
    const toolbarFrameRef = useRef<number>();
    const toolbarSignatureRef = useRef('');
    const toolbarReviewRef = useRef('');
    const streamEditRef = useRef<ReturnType<typeof createSummaryStreamEdit> | null>(null);
    const initialGenerationEditRef = useRef(false);
    const streamSourceRef = useRef('');
    const completedStreamRef = useRef(false);
    const queueRef = useRef<SummarySaveQueue | null>(null);
    const [draftNotice, setDraftNotice] = useState(false);
    const [conflictServer, setConflictServer] = useState<string | null>(null);
    const [hasStreamPreview, setHasStreamPreview] = useState(false);
    const [updateVersion, setUpdateVersion] = useState<SummaryUpdateVersion | null>(null);
    const [updateMessage, setUpdateMessage] = useState('');
    const changeScrollRef = useRef<ReturnType<typeof createSummaryChangeScroll> | null>(null);
    const lastSummaryUpdateIdRef = useRef<string | null>(null);
    const activeSummaryUpdateRef = useRef<{ id: string; terminal: boolean; previews: SummaryPreviewScheduler; measurement: SummaryUpdateMeasurement } | null>(null);
    const mountedRef = useRef(true);
    const summarySourceContent = getSummarySourceContent(summary);
    const summaryDomain = getDomain();
    const apiService = useMemo(() => new ApiService(), []);
    const generation = useSummaryGeneration({
        api: apiService,
        scope: JSON.stringify([summaryDomain, user?.id, summary.fileId, summary.meetingType]),
        fileId: summary.fileId,
        meetingType: summary.meetingType,
        templateUuid: summary.templateUuid,
        t,
        onBlock: () => {
            saveQueue.suspended = true;
            saveQueue.cancelTimers();
            editor?.setEditable(canEdit && isEditing && initialGenerationEditRef.current && !!streamEditRef.current);
            if (saveToHistoryTimeoutRef.current) clearTimeout(saveToHistoryTimeoutRef.current);
            setShowPromptBar(false); setShowFieldOverlay(false); setActiveTab('summary');
        },
        onStart: () => {
            const source = getCurrentEditorSourceContent();
            beginStreamEdit(source, saveQueue.pending && (saveQueue.conflict || saveQueue.restored));
            initialGenerationEditRef.current = true;
            hasInitializedEditorContentRef.current = true;
            if (editor) setSummaryGenerationStreaming(editor, true);
        },
        onPreview: source => {
            if (initialGenerationEditRef.current) applyStreamSource(source);
        },
        onResult: source => {
            if (editor) setSummaryGenerationStreaming(editor, false);
            // Keep the merge session alive through GET, including text typed
            // after the result arrives. Only the confirmed merge may be saved.
            applyStreamSource(source);
            lastSavedContentRef.current = source;
        },
        onFailure: () => {
            if (editor) setSummaryGenerationStreaming(editor, false);
            if (!initialGenerationEditRef.current || !streamEditRef.current) return;
            const hadUserChanges = streamEditRef.current.hasUserChanges;
            rollbackStreamEdit();
            initialGenerationEditRef.current = false;
            if (!hadUserChanges) { saveQueue.endStream(); saveQueue.adopt(lastSavedContentRef.current); }
            saveQueue.suspended = true;
            editor?.setEditable(false);
        },
        onLoaded: async (server, summaries, serverRecording) => {
            if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);
            saveQueue.materialize();
            const source = getSummarySourceContent(server);
            let merged = source;
            // A saved generation can be merged with the exact edits checkpointed
            // before navigation. The recovery document alone omits later AI text.
            if (!streamEditRef.current && saveQueue.stream && !saveQueue.storageConflict && editor) {
                const checkpoint = saveQueue.stream;
                const previous = editor.state.doc;
                try {
                    const document = editor.schema.nodeFromJSON(checkpoint.document);
                    document.check();
                    setEditorInitializingState(true);
                    editor.commands.setContent(document.toJSON(), false);
                    streamEditRef.current = createSummaryStreamEdit(editor, false, checkpoint);
                    initialGenerationEditRef.current = true;
                } catch {
                    editor.commands.setContent(previous.toJSON(), false);
                    // Older or incompatible checkpoints still require comparison.
                }
            }
            if (initialGenerationEditRef.current && streamEditRef.current) {
                merged = completeStreamEdit(source);
                initialGenerationEditRef.current = false;
            } else if (saveQueue.pending && (saveQueue.restored || saveQueue.conflict) &&
                (saveQueue.document || saveQueue.content !== saveQueue.confirmed)) {
                // Reopening or recovering must not replace edits checkpointed
                // during generation with the server's unedited result.
                setConflictServer(source);
                saveQueue.conflict = true;
                saveQueue.suspended = false;
                syncStructuredContentFromSource(saveQueue.content);
                applyEditorSourceContent(saveQueue.content);
                hasInitializedEditorContentRef.current = true;
                setEditorInitializingState(false);
                onGenerationReload?.(summaries, serverRecording, server.title);
                return;
            } else {
                saveQueue.endStream(); saveQueue.adopt(source);
                syncStructuredContentFromSource(source);
                applyEditorSourceContent(source);
                hydrateColumnWidths(extractColWidthTokensByTable(source));
            }
            hasInitializedEditorContentRef.current = true;
            setEditorInitializingState(false);
            lastSavedContentRef.current = source;
            rememberSelfPushedSourceContent(merged);
            onGenerationReload?.(summaries, serverRecording, server.title);
            onUpdate(merged === source ? server : { ...server, sourceContent: merged, content: parseStructuredFieldMarkdown(merged).cleanContent });
            addToHistory(merged, 'ai_improvement', 'Nieuwe AI samenvatting');
            if (saveQueue.pending) await flushManualSave();
        },
    });
    const isGenerationBlocked = generation.isBlocked;
    const positionScope = useMemo(() => ({ domain: summaryDomain, userId: user?.id ?? '', recordingId: summary.fileId, meetingType: summary.meetingType }), [summaryDomain, user?.id, summary.fileId, summary.meetingType]);
    const restoredPosition = useMemo(() => readSummaryEditorPosition(positionScope), [positionScope]);

    // State voor additional recordings van de summary (uit API)
    const [summaryAdditionalRecordings, setSummaryAdditionalRecordings] = useState<Recording[]>([]);
    const [showAdditionalRecordings, setShowAdditionalRecordings] = useState(false);
    const [isEditing, setIsEditing] = useState(() => canEdit === true);
    const [activeTab, setActiveTab] = useState<'summary' | 'fields'>('summary');
    const [, refreshSaveState] = useState(0);
    const [saveError, setSaveError] = useState(false);
    const saveBlockReasonRef = useRef<string | null>(null);
    const [showPromptBar, setShowPromptBar] = useState(false);
    const [promptValue, setPromptValue] = useState('');
    const [promptError, setPromptError] = useState('');
    const [draftReviewRequest, setDraftReviewRequest] = useState(0);
    const [selectedText, setSelectedText] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [isRefreshingSummary, setIsRefreshingSummary] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const deletingRef = useRef(false);
    const [deleteError, setDeleteError] = useState('');
    const isSummaryBusy = isStreaming || isRefreshingSummary || isDeleting || generation.blocked;
    const [streamAbortController, setStreamAbortController] = useState<AbortController | null>(null);
    const [justFinishedStreaming] = useState(false);
    const [error, setError] = useState('');
    const [canRetry, setCanRetry] = useState(false);
    const lastImprovementPromptRef = useRef<string | null>(null);
    const [showShareDialog, setShowShareDialog] = useState(false);
    // Momentopname van de editorinhoud op het moment van delen. Bewust niet
    // summary.content: dat veld wordt na lokale bewerkingen en AI-verbeteringen
    // niet bijgewerkt (alleen sourceContent), waardoor het klembord anders een
    // verouderde versie zou delen terwijl Word/PDF (via de server) wél actueel zijn.
    const [shareContent, setShareContent] = useState('');
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const initialStructuredContentRef = useRef(parseStructuredFieldMarkdown(summarySourceContent));
    const [editorCleanContent, setEditorCleanContent] = useState(initialStructuredContentRef.current.cleanContent);
    const [fieldAssignments, setFieldAssignments] = useState<FieldAssignment[]>(initialStructuredContentRef.current.assignments);
    const [showFieldOverlay, setShowFieldOverlay] = useState(false);
    const [streamStatusText, setStreamStatusText] = useState('');

    // Diff state for showing changes compared to previous AI-generated version
    const [showDiff, setShowDiff] = useState(false);
    // Track the last AI change (before and after content) - persists across user edits
    const lastAiChangeRef = useRef<{ beforeContent: string; afterContent: string } | null>(null);

    const lastAiSnapshotRef = useRef<{ source: string; document: object } | null>(null);

    // History state for undo/redo functionality
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [isApplyingHistory, setIsApplyingHistory] = useState(false);
    const [isInitializingEditor, setIsInitializingEditor] = useState(false);

    // New state for audio and transcript functionality
    const [transcript, setTranscript] = useState('');
    const [showTranscript, setShowTranscript] = useState(false);
    const [currentAudioTime, setCurrentAudioTime] = useState(0);
    const [, setAudioDuration] = useState(0);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);

    const isReadOnly = !canEdit || !isEditing;
    const canShareSummary = canDownload;
    const canShowFieldsTab = Boolean((summary.templateSchema?.fields?.length || 0) > 0 || (summary.extractedFieldItems?.length || 0) > 0);
    const canShowFormattingToolbar = canEdit && isEditing && !showFieldOverlay;

    const audioPlayerRef = useRef<AudioPlayerRef>(null);
    const editorHeaderRef = useRef<HTMLElement>(null);
    const editorToolbarRef = useRef<HTMLDivElement>(null);
    const streamingStartedRef = useRef<string | null>(null);
    const saveToHistoryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastSavedContentRef = useRef<string>(summarySourceContent);
    const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // De bron-markdown die de editor op dit moment weerspiegelt. Komt een
    // prop-update binnen met dezelfde inhoud, dan is dat een echo van onze eigen
    // opslag en mag het document niet opnieuw worden gezet.
    const editorSourceContentRef = useRef<string>(summarySourceContent);
    // Alle inhoud die deze editor zelf naar de parent heeft gestuurd. Een echo
    // kan later terugkomen dan de gebruiker doortypt, dus vergelijken met alleen
    // de huidige editor-inhoud is niet genoeg.
    const selfPushedSourceContentsRef = useRef<string[]>([]);
    const hasInitializedEditorContentRef = useRef(false);
    const recordingTitleUpdatedRef = useRef<boolean>(false); // Track if title was auto-updated
    const currentCleanContentRef = useRef(initialStructuredContentRef.current.cleanContent);
    const fieldAssignmentsRef = useRef<FieldAssignment[]>(initialStructuredContentRef.current.assignments);
    const isInitializingEditorRef = useRef(isInitializingEditor);
    const streamPhaseRef = useRef<0 | 1 | 2 | 3>(0);
    const streamPhaseTwoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const streamPhaseThreeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const pendingStreamContentUpdateRef = useRef(false);

    // Live-gemeten kolombreedtes (% per tabel, in documentvolgorde) die de
    // turndown-tabelregel uitleest bij het serialiseren naar markdown.
    const measuredColWidthsRef = useRef<(string | null)[][]>([]);
    const colWidthTableCounterRef = useRef<number>(0);

    const fieldItems = useMemo(() => {
        const schemaFields = summary.templateSchema?.fields || [];
        const extractedItems = summary.extractedFieldItems || [];
        const extractedMap = new Map(extractedItems.map((item) => [item.code, item]));

        const mergedFields = schemaFields.map((field) => {
            const extracted = extractedMap.get(field.code);

            return {
                code: field.code,
                label: extracted?.label || field.label || field.code,
                description: extracted?.description || field.description,
                value: extracted?.value ?? null,
                required: extracted?.required ?? field.required,
                multiline: extracted?.multiline ?? field.multiline,
                exportKey: extracted?.exportKey ?? field.exportKey,
            };
        });

        const extraExtractedFields = extractedItems.filter((item) => !schemaFields.some((field) => field.code === item.code));

        return [...mergedFields, ...extraExtractedFields];
    }, [summary.extractedFieldItems, summary.templateSchema]);

    const overlaySmartFields = useMemo<SmartTemplateField[]>(() => fieldItems.map((field) => ({
        code: field.code,
        label: field.label,
        description: field.description,
        required: field.required,
        multiline: field.multiline,
        exportKey: field.exportKey,
    })), [fieldItems]);

    const hasFieldAssignments = fieldAssignments.length > 0;
    const hasSourceFieldMarkers = useMemo(() => hasStructuredFieldMarkers(summarySourceContent), [summarySourceContent]);
    const canShowFieldOverlay = hasFieldAssignments || hasSourceFieldMarkers || canShowFieldsTab;

    const syncStructuredContentFromSource = useCallback((sourceContent: string) => {
        const parsed = parseStructuredFieldMarkdown(sourceContent);
        currentCleanContentRef.current = parsed.cleanContent;
        fieldAssignmentsRef.current = parsed.assignments;
        setEditorCleanContent(parsed.cleanContent);
        setFieldAssignments(parsed.assignments);
        return parsed;
    }, []);

    const composeEditorSourceContent = useCallback((cleanContent = currentCleanContentRef.current, assignments = fieldAssignmentsRef.current) => (
        composeStructuredFieldMarkdown(cleanContent, assignments)
    ), []);

    const setEditorInitializingState = useCallback((nextValue: boolean) => {
        isInitializingEditorRef.current = nextValue;
        setIsInitializingEditor(nextValue);
    }, []);

    const rememberSelfPushedSourceContent = useCallback((content: string) => {
        const normalized = normalizeSourceForComparison(content);
        const remaining = selfPushedSourceContentsRef.current.filter((entry) => entry !== normalized);
        // Alleen recente echo's zijn relevant; houd de lijst kort.
        selfPushedSourceContentsRef.current = [...remaining, normalized].slice(-10);
    }, []);

    const isSelfPushedSourceContent = useCallback((content: string) => (
        selfPushedSourceContentsRef.current.includes(normalizeSourceForComparison(content))
    ), []);

    // Een prop-update is een echo als de editor die inhoud al toont, of als we
    // die inhoud zelf eerder naar de parent hebben gestuurd.
    const isEchoOfEditorContent = useCallback((content: string) => (
        isSameSourceContent(content, editorSourceContentRef.current) || isSelfPushedSourceContent(content)
    ), [isSelfPushedSourceContent]);

    const clearStreamStatusSequence = useCallback(() => {
        if (streamPhaseTwoTimeoutRef.current) {
            clearTimeout(streamPhaseTwoTimeoutRef.current);
            streamPhaseTwoTimeoutRef.current = null;
        }

        if (streamPhaseThreeTimeoutRef.current) {
            clearTimeout(streamPhaseThreeTimeoutRef.current);
            streamPhaseThreeTimeoutRef.current = null;
        }

        pendingStreamContentUpdateRef.current = false;
        streamPhaseRef.current = 0;
    }, []);

    const resetStreamStatus = useCallback((statusText: string = '') => {
        clearStreamStatusSequence();
        setStreamStatusText(normalizeStreamFeedbackMessage(statusText));
    }, [clearStreamStatusSequence]);

    const startGenericStreamStatus = useCallback((statusText: string) => {
        clearStreamStatusSequence();
        streamPhaseRef.current = 1;
        setStreamStatusText(normalizeStreamFeedbackMessage(statusText));
    }, [clearStreamStatusSequence]);

    const markStreamContentUpdating = useCallback(() => {
        if (streamPhaseRef.current >= 3) {
            return;
        }

        if (streamPhaseRef.current >= 2) {
            if (streamPhaseThreeTimeoutRef.current) {
                clearTimeout(streamPhaseThreeTimeoutRef.current);
                streamPhaseThreeTimeoutRef.current = null;
            }

            pendingStreamContentUpdateRef.current = false;
            streamPhaseRef.current = 3;
            setStreamStatusText(t('summaryEditor.streamStatus.contentUpdating'));
            return;
        }

        pendingStreamContentUpdateRef.current = true;
    }, [t]);

    // Houd de sticky toolbar-offset gelijk aan de werkelijke headerhoogte,
    // zodat de toolbar nooit achter de (hogere) header verdwijnt bij scrollen.
    useEffect(() => {
        const header = editorHeaderRef.current;
        if (!header) return;

        const updateHeaderOffset = () => {
            header.parentElement?.style.setProperty('--editor-header-height', `${header.offsetHeight}px`);
        };

        updateHeaderOffset();
        const observer = new ResizeObserver(updateHeaderOffset);
        observer.observe(header);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (isReadOnly) {
            setShowPromptBar(false);
        }
    }, [isReadOnly]);

    useEffect(() => {
        setIsEditing(canEdit === true);
        setActiveTab('summary');
        setShowDiff(false);
        setShowFieldOverlay(false);
        resetStreamStatus();
        lastAiChangeRef.current = null;
    }, [canEdit, summary.fileId, summary.id, summary.meetingType, resetStreamStatus]);

    useEffect(() => {
        // Bij een echo van onze eigen opslag zijn de refs al actueel (of zelfs
        // verder dan de prop); opnieuw syncen zou de veldposities terugzetten
        // naar een gelijkwaardige serverversie.
        if (streamEditRef.current || queueRef.current?.pending || activeSummaryUpdateRef.current || isEchoOfEditorContent(summarySourceContent)) {
            return;
        }

        syncStructuredContentFromSource(summarySourceContent);
    }, [summarySourceContent, syncStructuredContentFromSource, isEchoOfEditorContent]);

    useEffect(() => {
        isInitializingEditorRef.current = isInitializingEditor;
    }, [isInitializingEditor]);

    useEffect(() => {
        if (!activeSummaryUpdateRef.current && !streamEditRef.current && !queueRef.current?.pending) lastSavedContentRef.current = summarySourceContent;
    }, [summarySourceContent]);

    useEffect(() => {
        if (!canShowFieldOverlay && showFieldOverlay) {
            setShowFieldOverlay(false);
        }
    }, [canShowFieldOverlay, showFieldOverlay]);

    useEffect(() => {
        if (!canShowFieldsTab && activeTab === 'fields') {
            setActiveTab('summary');
        }
    }, [activeTab, canShowFieldsTab]);

    useEffect(() => {
        if (!canShareSummary) {
            setShowShareDialog(false);
        }
    }, [canShareSummary]);

    useEffect(() => {
        setChatMessages([]);
        setIsChatOpen(false);
    }, [summary.id, summary.fileId]);

    // History management functions
    const addToHistory = (content: string, type: HistoryEntry['type'], description?: string) => {
        // Check if content is different from the current history entry
        const currentEntry = history[historyIndex];

        logger.log('🔄 HISTORY: addToHistory called', {
            type,
            description,
            contentLength: content.length,
            currentHistoryIndex: historyIndex,
            historyLength: history.length,
            currentEntryExists: !!currentEntry,
            lastEntryExists: !!(history[history.length - 1]),
            contentChanged: !currentEntry || currentEntry.content !== content,
            isApplyingHistory
        });

        // Check against current history entry
        if (currentEntry && currentEntry.content === content) {
            // console.log('🔄 HISTORY: Content is identical to current history entry, skipping...', {
            //     currentEntryType: currentEntry.type,
            //     currentEntryDescription: currentEntry.description
            // });
            return; // Don't add identical content
        }

        // Also check against the last entry in history (which might be different from current)
        const lastEntry = history[history.length - 1];
        if (lastEntry && lastEntry.content === content) {
            // console.log('🔄 HISTORY: Content is identical to last history entry, skipping...', {
            //     lastEntryType: lastEntry.type,
            //     lastEntryDescription: lastEntry.description
            // });
            return; // Don't add identical content
        }

        // Extra check for rapid successive changes (within 500ms) of same type
        if (currentEntry && currentEntry.type === type && type === 'user_edit') {
            const timeDiff = Date.now() - new Date(currentEntry.timestamp).getTime();
            if (timeDiff < 500) {
                // console.log('🔄 HISTORY: Rapid successive user edit detected, skipping to prevent spam', {
                //     timeDiff,
                //     currentEntryTimestamp: currentEntry.timestamp
                // });
                return;
            }
        }

        const newEntry: HistoryEntry = {
            content,
            timestamp: new Date().toISOString(),
            type,
            description
        };

        // console.log('🔄 HISTORY: Adding new entry', {
        //     entryType: newEntry.type,
        //     entryDescription: newEntry.description,
        //     beforeHistoryLength: history.length,
        //     willTruncateAfterIndex: historyIndex
        // });

        setHistory(prev => {
            // Remove everything after current index (when adding after undo)
            const newHistory = prev.slice(0, historyIndex + 1);
            newHistory.push(newEntry);

            // console.log('🔄 HISTORY: New history state', {
            //     previousLength: prev.length,
            //     newLength: newHistory.length,
            //     truncatedEntries: prev.length - (historyIndex + 1),
            //     addedEntry: true
            // });

            // Keep max 50 entries to prevent memory issues
            if (newHistory.length > 50) {
                const trimmedHistory = newHistory.slice(-50);
                // console.log('🔄 HISTORY: Trimmed history to 50 entries', {
                //     beforeTrim: newHistory.length,
                //     afterTrim: trimmedHistory.length
                // });
                // Update index accordingly when trimming
                const indexOffset = newHistory.length - 50;
                setHistoryIndex(prev => {
                    const adjustedIndex = Math.max(0, prev - indexOffset + 1);
                    // console.log('🔄 HISTORY: Adjusted history index for trimming', {
                    //     oldIndex: prev,
                    //     indexOffset,
                    //     adjustedIndex
                    // });
                    return adjustedIndex;
                });
                return trimmedHistory;
            } else {
                // Normal case: set index to last position
                setHistoryIndex(() => {
                    const newIndex = newHistory.length - 1;
                    // console.log('🔄 HISTORY: Updated history index', {
                    //     newIndex,
                    //     historyLength: newHistory.length
                    // });
                    return newIndex;
                });
                return newHistory;
            }
        });
    };

    // Ensure history index is always valid
    const validHistoryIndex = Math.max(0, Math.min(historyIndex, history.length - 1));
    const canUndo = validHistoryIndex > 0 && history.length > 1;
    const canRedo = validHistoryIndex < history.length - 1;

    // Fix invalid history index automatically but only log once
    React.useEffect(() => {
        if (validHistoryIndex !== historyIndex && !isApplyingHistory) {
            logger.warn('🔄 HISTORY: Auto-correcting invalid history index', {
                invalidIndex: historyIndex,
                correctedIndex: validHistoryIndex,
                historyLength: history.length
            });
            setHistoryIndex(validHistoryIndex);
        }
    }, [validHistoryIndex, historyIndex, history.length, isApplyingHistory]);

    // Note: Diff functionality now uses history instead of preAiContent

    const performUndo = () => {
        if (isGenerationBlocked() && !initialGenerationEditRef.current) return;
        if (editor?.can().undo()) { editor.commands.undo(); return; }
        if (streamEditRef.current) return;
        // console.log('🔄 HISTORY: performUndo called', {
        //     canUndo,
        //     isApplyingHistory,
        //     currentIndex: historyIndex,
        //     validIndex: validHistoryIndex,
        //     historyLength: history.length
        // });

        if (!canUndo || isApplyingHistory) {
            // console.log('🔄 HISTORY: Undo blocked', { canUndo, isApplyingHistory });
            return;
        }

        const newIndex = validHistoryIndex - 1;
        const entry = history[newIndex];

        // console.log('🔄 HISTORY: Performing undo', {
        //     fromIndex: historyIndex,
        //     toIndex: newIndex,
        //     entryExists: !!entry,
        //     entryType: entry?.type,
        //     entryDescription: entry?.description,
        //     contentLength: entry?.content?.length || 0
        // });

        if (entry && editor) {
            setIsApplyingHistory(true);
            syncStructuredContentFromSource(entry.content);
            applyEditorSourceContent(entry.content);
            setHistoryIndex(newIndex);
            saveQueue.stage(entry.content, editor?.getJSON());

            // Update summary state
            const updatedSummary = {
                ...summary,
                sourceContent: entry.content,
                updatedAt: new Date().toISOString()
            };
            rememberSelfPushedSourceContent(entry.content);
            onUpdate(updatedSummary);

            // console.log('🔄 HISTORY: Undo applied', {
            //     newIndex,
            //     newCanRedo: newIndex < history.length - 1,
            //     newCanUndo: newIndex > 0
            // });

            setTimeout(() => {
                setIsApplyingHistory(false);

                // Als diff view actief is, pas deze opnieuw toe na history cycling
                if (showDiff) {
                    setTimeout(() => {
                        const previousContent = history[newIndex - 1]?.content;
                        if (previousContent) {
                            applyDiffMarks(editor, previousContent, entry.content, markdownToHtml);
                        }
                    }, 50);
                }

                // console.log('🔄 HISTORY: Undo application completed, history tracking re-enabled');
            }, 100);
        }
    };

    const performRedo = () => {
        if (isGenerationBlocked() && !initialGenerationEditRef.current) return;
        if (editor?.can().redo()) { editor.commands.redo(); return; }
        if (streamEditRef.current) return;
        // console.log('🔄 HISTORY: performRedo called', {
        //     canRedo,
        //     isApplyingHistory,
        //     currentIndex: historyIndex,
        //     validIndex: validHistoryIndex,
        //     historyLength: history.length
        // });

        if (!canRedo || isApplyingHistory) {
            // console.log('🔄 HISTORY: Redo blocked', { canRedo, isApplyingHistory });
            return;
        }

        const newIndex = validHistoryIndex + 1;
        const entry = history[newIndex];

        // console.log('🔄 HISTORY: Performing redo', {
        //     fromIndex: historyIndex,
        //     toIndex: newIndex,
        //     entryExists: !!entry,
        //     entryType: entry?.type,
        //     entryDescription: entry?.description,
        //     contentLength: entry?.content?.length || 0
        // });

        if (entry && editor) {
            setIsApplyingHistory(true);
            syncStructuredContentFromSource(entry.content);
            applyEditorSourceContent(entry.content);
            setHistoryIndex(newIndex);
            saveQueue.stage(entry.content, editor?.getJSON());

            // Update summary state
            const updatedSummary = {
                ...summary,
                sourceContent: entry.content,
                updatedAt: new Date().toISOString()
            };
            rememberSelfPushedSourceContent(entry.content);
            onUpdate(updatedSummary);

            // console.log('🔄 HISTORY: Redo applied', {
            //     newIndex,
            //     newCanRedo: newIndex < history.length - 1,
            //     newCanUndo: newIndex > 0
            // });

            setTimeout(() => {
                setIsApplyingHistory(false);

                // Als diff view actief is, pas deze opnieuw toe na history cycling
                if (showDiff) {
                    setTimeout(() => {
                        const previousContent = history[newIndex - 1]?.content;
                        if (previousContent) {
                            applyDiffMarks(editor, previousContent, entry.content, markdownToHtml);
                        }
                    }, 50);
                }

                // console.log('🔄 HISTORY: Redo application completed, history tracking re-enabled');
            }, 100);
        }
    };

    // Debounced function to save user edits to history
    const debouncedSaveToHistory = (content: string) => {
        const currentValidIndex = Math.max(0, Math.min(historyIndex, history.length - 1));
        const currentEntry = history[currentValidIndex];
        const isContentDifferent = content !== (currentEntry?.content || '');

        // console.log('🔄 HISTORY: debouncedSaveToHistory called', {
        //     contentLength: content.length,
        //     currentHistoryIndex: historyIndex,
        //     validHistoryIndex: currentValidIndex,
        //     isApplyingHistory,
        //     isContentDifferent,
        //     hasPendingTimeout: !!saveToHistoryTimeoutRef.current,
        //     currentEntryType: currentEntry?.type
        // });

        // Early return if applying history, content is identical, or just finished streaming
        if (isApplyingHistory) {
            // console.log('🔄 HISTORY: Skipping debounced save - applying history');
            return;
        }

        if (justFinishedStreaming) {
            // console.log('🔄 HISTORY: Skipping debounced save - just finished streaming');
            return;
        }

        if (!isContentDifferent) {
            // console.log('🔄 HISTORY: Skipping debounced save - content identical');
            return;
        }

        // Clear existing timeout if any
        if (saveToHistoryTimeoutRef.current) {
            // console.log('🔄 HISTORY: Clearing existing debounced timeout');
            clearTimeout(saveToHistoryTimeoutRef.current);
        }

        // Set new timeout
        saveToHistoryTimeoutRef.current = setTimeout(() => {
            // console.log('🔄 HISTORY: Debounced timeout triggered, performing final checks');

            // Stop hier als we al bezig zijn met het toepassen van history
            if (isApplyingHistory) {
                logger.log('🔄 HISTORY: Skipping - currently applying history');
                saveToHistoryTimeoutRef.current = null;
                return;
            }

            // Final double check with current valid index
            const finalValidIndex = Math.max(0, Math.min(historyIndex, history.length - 1));
            const finalCurrentEntry = history[finalValidIndex];

            // Extra veiligheidscheck: vergelijk ook met de laatste entry in history
            const lastEntry = history[history.length - 1];
            const isDifferentFromCurrent = content !== (finalCurrentEntry?.content || '');
            const isDifferentFromLast = content !== (lastEntry?.content || '');

            if (isDifferentFromCurrent && isDifferentFromLast) {
                // console.log('🔄 HISTORY: Final checks passed, adding user edit to history');
                addToHistory(content, 'user_edit', 'Gebruikersaanpassing');
            } else {
                // console.log('🔄 HISTORY: Final checks failed, skipping user edit', {
                //     isDifferentFromCurrent,
                //     isDifferentFromLast,
                //     finalValidIndex,
                //     historyLength: history.length
                // });
            }

            // Clear the ref
            saveToHistoryTimeoutRef.current = null;
        }, 1500); // Save to history after 1.5 seconds of inactivity

        // console.log('🔄 HISTORY: Set new debounced timeout (1000ms)');
    };

    const saveCallbacksRef = useRef({ summary, onUpdate, userId: user?.id, domain: summaryDomain, canEdit });
    saveCallbacksRef.current = { summary, onUpdate, userId: user?.id, domain: summaryDomain, canEdit };
    const saveQueue = useMemo(() => new SummarySaveQueue(summarySourceContent, async content => {
        const owner = saveCallbacksRef.current;
        if (!mountedRef.current || !owner.canEdit || owner.userId !== user?.id || owner.domain !== summaryDomain) throw new Error('Editor session closed');
        if (queueRef.current?.restored || queueRef.current?.failed) {
            const server = await apiService.getSummary(summary.fileId, summary);
            if (!server) throw new Error('Cannot verify saved report');
            if (getSummarySourceContent(server) !== queueRef.current.confirmed) {
                setConflictServer(getSummarySourceContent(server));
                throw new SummaryDraftConflictError('Server report changed');
            }
        }
        if (!mountedRef.current || saveCallbacksRef.current.userId !== user?.id || saveCallbacksRef.current.domain !== summaryDomain) throw new Error('Editor session closed');
        const saved = await apiService.updateSummary(summary.fileId, summary, content);
        if (!saved) throw new Error('Summary save failed');
        if (mountedRef.current && saveCallbacksRef.current.summary.fileId === summary.fileId && saveCallbacksRef.current.summary.meetingType === summary.meetingType) {
            lastSavedContentRef.current = content;
            rememberSelfPushedSourceContent(content);
            const latest = saveCallbacksRef.current;
            latest.onUpdate({ ...latest.summary, sourceContent: content,
                content: parseStructuredFieldMarkdown(content).cleanContent, updatedAt: new Date().toISOString() });
        }
    // Identity-scoped checkpoints survive navigation; detached editors cannot start another upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, canEdit && user?.id ? createSummaryDraftStore(positionScope) : undefined, JSON.stringify(positionScope)), [apiService, summary.fileId, summary.meetingType, user?.id, summaryDomain, canEdit]);
    queueRef.current = saveQueue;
    useEffect(() => {
        saveQueue.attach();
        let active = true;
        let retry: ReturnType<typeof setTimeout> | undefined;
        void saveQueue.ready.then(() => {
            if (!active) return;
            setDraftNotice(saveQueue.restored); refreshSaveState(value => value + 1);
            if (saveQueue.restored && !saveQueue.conflict) retry = setTimeout(() => { void saveQueue.flush(); }, 1000);
        });
        return () => { active = false; clearTimeout(retry); saveQueue.detach(); };
    }, [saveQueue]);
    useEffect(() => {
        if (!isGenerationBlocked() && !streamEditRef.current && !activeSummaryUpdateRef.current && saveQueue.loaded) saveQueue.reconcile(summarySourceContent);
    }, [saveQueue, summarySourceContent, isGenerationBlocked]);
    useEffect(() => saveQueue.subscribe(() => {
        setSaveError(saveQueue.failed);
        refreshSaveState(value => value + 1);
    }), [saveQueue]);
    const isSaving = useDelayedStatus(saveQueue.saving ? 'saving' : null) !== null;
    useEffect(() => {
        const guard = (event: BeforeUnloadEvent) => {
            saveQueue.materialize();
            if ((saveQueue.pending && !saveQueue.durable) || activeSummaryUpdateRef.current || streamEditRef.current || isGenerationBlocked()) {
                event.preventDefault(); event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', guard);
        const retry = () => { if (!streamEditRef.current) void saveQueue.flush(); };
        window.addEventListener('online', retry);
        const checkpoint = () => { if (document.visibilityState === 'hidden') saveQueue.materialize(); };
        document.addEventListener('visibilitychange', checkpoint);
        return () => { document.removeEventListener('visibilitychange', checkpoint); window.removeEventListener('beforeunload', guard); window.removeEventListener('online', retry); saveQueue.cancelTimers(); };
    }, [saveQueue, isGenerationBlocked]);
    const debouncedAutoSave = (content: string) => {
        if (activeSummaryUpdateRef.current || isApplyingHistory || isStreaming || isInitializingEditor || justFinishedStreaming || isReadOnly) return;
        saveQueue.stage(content, editor?.getJSON());
    };
    const flushManualSave = async () => {
        saveBlockReasonRef.current = null;
        saveQueue.materialize();
        let recoveryAttempted = false;
        const recover = async () => {
            if (recoveryAttempted || !saveQueue.conflict || saveQueue.storageConflict || saveQueue.suspended) return false;
            recoveryAttempted = true;
            await recoverDraftConflict();
            return true;
        };
        await recover();
        let saved = await saveQueue.flush();
        // A retrying save can discover a newer server version during its own check.
        if (!saved && await recover()) saved = await saveQueue.flush();
        if (!saved) { setSaveError(true); return false; }
        setSaveError(false);
        return true;
    };

    const handleFieldAssignmentsChange = (nextAssignments: FieldAssignment[]) => {
        fieldAssignmentsRef.current = nextAssignments;
        setFieldAssignments(nextAssignments);

        const sourceContent = composeEditorSourceContent(currentCleanContentRef.current, nextAssignments);

        if (editor && !isInitializingEditorRef.current) {
            setEditorInitializingState(true);
            applyEditorSourceContent(sourceContent, { preserveSelection: true });
            setTimeout(() => setEditorInitializingState(false), 200);
        }

        if (!isApplyingHistory && !isStreaming && !isInitializingEditorRef.current) {
            debouncedSaveToHistory(sourceContent);
            debouncedAutoSave(sourceContent);
        }
    };

    // Set audio URL and load transcript when component mounts
    useEffect(() => {
        // Set the audio URL
        setAudioUrl(`/audio/${recording.id}`);

        const loadTranscript = async () => {
            try {
                const transcriptData = await apiService.getTranscript(recording.id);
                setTranscript(transcriptData);
            } catch (error) {
                logger.error('Error loading transcript:', error);
            }
        };

        const loadSummaryAdditionalRecordings = async () => {
            // Laad additional recordings van de summary (uit API)
            if (summary.additionalRecordings && summary.additionalRecordings.length > 0) {
                try {
                    const recordingPromises = summary.additionalRecordings.map(recordingId =>
                        apiService.getFile(recordingId)
                    );
                    const recordings = await Promise.all(recordingPromises);
                    // Filter out null results
                    const validRecordings = recordings.filter(r => r !== null) as Recording[];
                    setSummaryAdditionalRecordings(validRecordings);
                } catch (error) {
                    logger.error('Error loading additional recordings:', error);
                }
            } else {
                setSummaryAdditionalRecordings([]);
            }
        };

        loadTranscript();
        loadSummaryAdditionalRecordings();
    }, [apiService, recording.id, summary.additionalRecordings]);

    // Initialize turndown service for HTML to Markdown conversion
    const turndownService = useMemo(() => {
        const service = new TurndownService({
            headingStyle: 'atx',
            bulletListMarker: '*',
            codeBlockStyle: 'fenced'
        });

        // Prevent TurndownService from escaping [ and ] characters.
        // The default escape function turns "[ ] Intake gesprek" into
        // "\[ \] Intake gesprek" on every HTML→markdown round-trip.
        const originalEscape = service.escape.bind(service);
        service.escape = (text: string) => {
            const escaped = originalEscape(text);
            // Restore [ and ] — they are safe in our content context
            return escaped.replace(/\\\[/g, '[').replace(/\\\]/g, ']');
        };

        // Custom rule to filter out empty list items
        service.addRule('filterEmptyListItems', {
            filter: 'li',
            replacement: function (content, node) {
                const trimmedContent = content.trim();
                if (!trimmedContent || trimmedContent === '') {
                    return ''; // Skip empty list items completely
                }
                return '* ' + trimmedContent + '\n';
            }
        });

        // Custom rule for table conversion (Markdown) + col width metadata
        service.addRule('tables', {
            filter: 'table',
            replacement: function (content, node) {
                const cellMarkdown = (cell: Element) => service.turndown(cell.innerHTML)
                    .trim().replace(/\|/g, '\\|').replace(/\n+/g, '<br>');
                const thead = node.querySelector('thead');
                const tbody = node.querySelector('tbody');

                let markdown = '\n';
                let headerCount = 0;

                // Kolombreedtes komen uit de live DOM-meting (measureColWidthTokens),
                // gekoppeld per tabel-index in documentvolgorde. Set-kolommen zijn een
                // percentage van de tabelbreedte; niet-aangepaste kolommen zijn `null`.
                const measuredWidths = measuredColWidthsRef.current[colWidthTableCounterRef.current] || null;
                colWidthTableCounterRef.current += 1;

                const colWidthComment = measuredWidths ? buildColWidthComment(measuredWidths) : null;
                if (colWidthComment) {
                    markdown += `${colWidthComment}\n`;
                }

                // Process header row
                if (thead) {
                    const headerRow = thead.querySelector('tr');
                    if (headerRow) {
                        const headers = Array.from(headerRow.querySelectorAll('th')).map(cellMarkdown);
                        headerCount = headers.length;

                        // Header row
                        markdown += '| ' + headers.join(' | ') + ' |\n';

                        // Separator row
                        markdown += '|' + headers.map(() => ' --- ').join('|') + '|\n';
                    }
                }

                // Process body rows (including header row if no thead exists)
                const allRows = tbody ?
                    Array.from(tbody.querySelectorAll('tr')) :
                    Array.from(node.querySelectorAll('tr'));

                if (allRows.length > 0 && !thead) {
                    // If no thead, treat first row as header
                    const firstRow = allRows[0];
                    const headers = Array.from(firstRow.querySelectorAll('td, th')).map(cellMarkdown);
                    headerCount = headers.length;

                    markdown += '| ' + headers.join(' | ') + ' |\n';
                    markdown += '|' + headers.map(() => ' --- ').join('|') + '|\n';

                    // Process remaining rows
                    for (let i = 1; i < allRows.length; i++) {
                        const cells = Array.from(allRows[i].querySelectorAll('td')).map(cellMarkdown);
                        // Pad cells to match header count
                        while (cells.length < headerCount) cells.push('');
                        markdown += '| ' + cells.slice(0, headerCount).join(' | ') + ' |\n';
                    }
                } else if (tbody) {
                    // Process tbody rows
                    allRows.forEach(row => {
                        const cells = Array.from(row.querySelectorAll('td')).map(cellMarkdown);
                        // Pad cells to match header count
                        while (cells.length < headerCount) cells.push('');
                        markdown += '| ' + cells.slice(0, headerCount).join(' | ') + ' |\n';
                    });
                }

                return markdown + '\n';
            }
        });

        return service;
    }, []);

    // Convert markdown to HTML for Tiptap
    const markdownToHtml = (markdown: string): string => {
        const visibleMarkdown = parseStructuredFieldMarkdown(normalizeSummaryMarkdown(markdown)).cleanContent;

        if (!visibleMarkdown) {
            return '';
        }

        const processInlineMarkdown = (text: string) => {
            // Eerst unescape om dubbele escaping te voorkomen
            let processed = text
                .replace(/\\(\*)/g, '$1')  // \* → *
                .replace(/\\(_)/g, '$1')   // \_ → _
                .replace(/\\(`)/g, '$1')   // \` → `
                .replace(/\\(\|)/g, '$1')  // \| → |
                .replace(/\\(-)/g, '$1')   // \- → -
                .replace(/\\\./g, '.')    // \\. → .
                .replace(/\\\[/g, '[')     // \[ → [
                .replace(/\\\]/g, ']')     // \] → ]
                .replace(/\\\\/g, '\\');   // \\ → \

            // Dan markdown processing - ondersteun zowel * als _ syntaxen
            return processed
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')  // **bold**
                .replace(/__([^_]+?)__/g, '<strong>$1</strong>')   // __bold__
                .replace(/\*([^*]+?)\*/g, '<em>$1</em>')           // *italic*
                .replace(/_([^_]+?)_/g, '<em>$1</em>')             // _italic_ ← Deze ontbrak!
                .replace(/`([^`]+?)`/g, '<code>$1</code>');        // `code`
        };

        // Strip escaped blockquote markers (\> → >) at the start of lines
        const preprocessedMarkdown = visibleMarkdown.replace(/^\\>/gm, '>');

        const lines = collapseListItemBlankLines(preprocessedMarkdown).split('\n');
        let html = '';
        let inList = false;
        let listType: 'ul' | 'ol' | null = null;
        let inTable = false;
        let tableRows: string[] = [];

        const closeList = () => {
            if (inList && listType) {
                html += `</${listType}>\n`;
                inList = false;
                listType = null;
            }
        };

        const closeTable = () => {
            if (inTable && tableRows.length > 0) {
                // Kolombreedtes worden niet in de HTML geïnjecteerd: Tiptap
                // genereert zelf de colgroup en de px-breedtes worden na het
                // laden gehydrateerd uit de colwidth-comment (hydrateColumnWidths).
                html += '<table>\n';

                // Process header row (first row)
                if (tableRows.length > 0) {
                    const headerCells = tableRows[0].split('|').map(cell => cell.trim()).filter(cell => cell !== '');
                    html += '<thead>\n<tr>\n';
                    headerCells.forEach((cell) => {
                        html += `<th>${processInlineMarkdown(cell)}</th>\n`;
                    });
                    html += '</tr>\n</thead>\n';
                }

                // Process body rows (skip separator row if exists)
                if (tableRows.length > 1) {
                    html += '<tbody>\n';
                    const startIndex = tableRows.length > 1 && tableRows[1].includes('---') ? 2 : 1;

                    for (let i = startIndex; i < tableRows.length; i++) {
                        const cells = tableRows[i].split('|').map(cell => cell.trim()).filter(cell => cell !== '');
                        if (cells.length > 0) {
                            html += '<tr>\n';
                            html += cells.map(cell => `<td>${processInlineMarkdown(cell)}</td>\n`).join('');
                            html += '</tr>\n';
                        }
                    }
                    html += '</tbody>\n';
                }

                html += '</table>\n';
                tableRows = [];
                inTable = false;
            }
        };

        for (let i = 0; i < lines.length; i++) {

            const line = lines[i];
            const trimmedLine = line.trim();
            // console.log('trimmedLine', trimmedLine);

            // Skip de colwidth metadata-comment; de breedtes worden los van de
            // HTML gehydrateerd (zie hydrateColumnWidths / extractColWidthTokensByTable).
            if (isColWidthComment(trimmedLine)) {
                continue;
            }


            // Check for table rows (lines containing |)
            const isTableRow = trimmedLine.includes('|') && trimmedLine.split('|').length > 2;
            const isTableSeparator = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(trimmedLine);

            if (isTableRow || isTableSeparator) {
                if (!inTable) {
                    closeList();
                    inTable = true;
                }

                if (!isTableSeparator) {
                    tableRows.push(trimmedLine);
                }
                continue;
            } else {
                // Close table if we were in one
                if (inTable) {
                    closeTable();
                }
            }

            // Headers (the hash may be escaped, "\###", by a markdown round-trip)
            const headingMatch = trimmedLine.match(/^\\?(#+)\s*(.*)/);
            if (headingMatch && headingMatch[1].length > 0) {
                closeList();
                const level = headingMatch[1].length;
                if (level <= 4) {
                    html += `<h${level}>${processInlineMarkdown(headingMatch[2].trim())}</h${level}>\n`;
                }
                continue;
            }

            // Blockquote
            if (trimmedLine.startsWith('>')) {
                closeList();
                const content = trimmedLine.replace(/^>\s?/, '');
                html += `<blockquote><p>${processInlineMarkdown(content)}</p></blockquote>\n`;
                continue;
            }

            // Unordered list (support both * and - with optional escaping)
            const ulMatch = line.match(/^\s*\\?[-*]\s+(.*)/);
            if (ulMatch) {
                const content = ulMatch[1];
                if (content.trim() === '') continue;

                // Handle "* ### Heading" — a heading authored as a list item.
                // Render it as a real heading block instead of literal "### " text.
                // The hash may be escaped ("\###") by an earlier markdown round-trip.
                const headingInList = content.trim().match(/^\\?(#{1,4})\s+(.*)/);
                if (headingInList) {
                    closeList();
                    const level = headingInList[1].length;
                    html += `<h${level}>${processInlineMarkdown(headingInList[2].trim())}</h${level}>\n`;
                    continue;
                }

                if (!inList || listType !== 'ul') {
                    closeList();
                    html += '<ul>\n';
                    inList = true;
                    listType = 'ul';
                }
                html += `<li>${processInlineMarkdown(content)}</li>\n`;
                continue;
            }

            // Ordered list
            const olMatch = line.match(/^\s*\d+\.\s+(.*)/);
            if (olMatch) {
                const content = olMatch[1];
                if (content.trim() === '') continue;

                if (!inList || listType !== 'ol') {
                    closeList();
                    html += '<ol>\n';
                    inList = true;
                    listType = 'ol';
                }
                html += `<li>${processInlineMarkdown(content)}</li>\n`;
                continue;
            }

            // Lege regels binnen een lijst sluiten de lijst niet af, zodat
            // items met dubbele newlines ertussen aaneengesloten renderen
            if (!trimmedLine && inList) {
                continue;
            }

            closeList();

            // Paragraphs and other text
            if (trimmedLine) {
                html += `<p>${processInlineMarkdown(trimmedLine)}</p>\n`;
            }
        }

        closeList();
        closeTable(); // Close any remaining table

        return html.trim();
    };

    // Initialize Tiptap editor
    const [initialEditorHtml] = useState(() => markdownToHtml(summarySourceContent));
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4],
                },
            }),
            Link.configure({
                openOnClick: false,
            }),
            Placeholder.configure({
                placeholder: ({ node }: { node: any }) => {
                    if (node.type.name === 'heading') {
                        return t('summaryEditor.placeholders.heading');
                    }
                    return t('summaryEditor.placeholders.body');
                },
            }),
            // Native resizable tabellen: Tiptap beheert kolombreedtes als px op
            // de cellen. De %-waarden worden bij laden/opslaan geconverteerd
            // (hydrateColumnWidths / measureColWidthTokens).
            SummaryTable.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            AiChange,
            SummaryGeneration,
            SummaryReview.configure({
                labels: () => ({ pending: t('summaryEditor.changes.pending'), changed: t('summaryEditor.changes.changed'), deleted: t('summaryEditor.changes.deleted') }),
                blocked: () => setReviewBlocked(true),
            }),
            SelectionContextHighlight,
        ],
        shouldRerenderOnTransaction: false,
        content: initialEditorHtml,
        editable: false,
        editorProps: {
            attributes: {
                class: 'notion-editor focus:outline-none',
            },
            transformPastedHTML: (html: string) => {
                // Filter out mark tags with ai-change class when pasting
                return html.replace(/<mark[^>]*class="[^"]*ai-change[^"]*"[^>]*>(.*?)<\/mark>/gi, '$1');
            },
        },
        onSelectionUpdate: ({ editor }) => {
            const { from, to } = editor.state.selection;
            const text = editor.state.doc.textBetween(from, to, ' ');
            setSelectedText(text);

            // Zodra de gebruiker in de editor zelf een nieuwe selectie maakt
            // (of klikt), is de vastgehouden context-highlight achterhaald.
            const decorations = selectionContextPluginKey.getState(editor.state);
            if (decorations && decorations.find().length > 0) {
                editor.view.dispatch(editor.state.tr.setMeta(selectionContextPluginKey, { clear: true }));
            }
        },
        onTransaction: ({ editor, transaction }) => {
            streamEditRef.current?.recordTransaction(transaction);
            if (toolbarFrameRef.current !== undefined) return;
            toolbarFrameRef.current = requestAnimationFrame(() => {
                toolbarFrameRef.current = undefined;
                if (editor.isDestroyed) return;
                const signature = JSON.stringify([
                    ...['bold', 'italic', 'strike', 'bulletList', 'orderedList', 'blockquote', 'table', 'heading'].map(name => editor.isActive(name)),
                    editor.getAttributes('heading').level, editor.getAttributes('link').href,
                    undoDepth(editor.state) > 0, redoDepth(editor.state) > 0,
                ]);
                const review = summaryReviewKey.getState(editor.state);
                const reviewSignature = review ? JSON.stringify([review.visible, review.active, review.highlighted,
                    review.pending.length > 0, review.selected, summaryReviewGroups(editor.state.doc, review).map(group => group.id)]) : '';
                if (signature !== toolbarSignatureRef.current || reviewSignature !== toolbarReviewRef.current) {
                    toolbarSignatureRef.current = signature;
                    toolbarReviewRef.current = reviewSignature;
                    refreshEditorUI(value => value + 1);
                }
            });
        },
        onUpdate: ({ editor, transaction }) => {
            // Changing editability also emits onUpdate; keep the existing checkpoint intact.
            if (!transaction.docChanged || deletingRef.current) return;
            if ((generation.isBlocked() && saveQueue.suspended && !streamEditRef.current) || !hasInitializedEditorContentRef.current || streamEditRef.current?.applying || isInitializingEditorRef.current || !canEdit) return;
            if (!streamEditRef.current && activeSummaryUpdateRef.current && !completedStreamRef.current) return;
            // ProseMirror nodes are immutable: retain the latest node without walking
            // the document, laying out tables or serializing HTML on each keystroke.
            const edit = streamEditRef.current;
            // Tiptap also emits onUpdate when editability changes; that is not
            // a user edit and must not turn an empty reservation into a draft.
            if (edit && !edit.hasUserChanges) return;
            const document = edit?.recoveryDocument ?? editor.state.doc;
            const checkpoint = edit?.captureCheckpoint();
            const source = edit ? streamSourceRef.current : editorSourceContentRef.current ?? summarySourceContent;
            saveQueue.defer(() => {
                const content = serializeStreamDocument(document, source);
                if (!edit) {
                    const parsed = parseStructuredFieldMarkdown(content);
                    currentCleanContentRef.current = parsed.cleanContent;
                    fieldAssignmentsRef.current = parsed.assignments;
                    editorSourceContentRef.current = content;
                    debouncedSaveToHistory(content);
                }
                return { content, document: document.toJSON(), stream: checkpoint?.() };
            });
        },
    });

    useEffect(() => {
        if (!editor) return;
        const toolbar = editorToolbarRef.current;
        if (!toolbar) return;
        const measure = () => toolbar.parentElement?.style.setProperty('--editor-toolbar-height', `${toolbar.offsetHeight}px`);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(toolbar);
        return () => observer.disconnect();
    }, [editor]);

    useEffect(() => {
        if (!editor) return;
        const scroller = editor.view.dom.closest('main');
        let timer: ReturnType<typeof setTimeout> | undefined;
        const savePosition = () => {
            if (editor.isDestroyed || isInitializingEditorRef.current || !hasInitializedEditorContentRef.current) return;
            const { anchor, head } = editor.state.selection;
            writeSummaryEditorPosition(positionScope, { anchor, head, scrollTop: scroller?.scrollTop ?? 0 });
        };
        const schedule = () => { clearTimeout(timer); timer = setTimeout(savePosition, 250); };
        editor.on('selectionUpdate', schedule);
        scroller?.addEventListener('scroll', schedule, { passive: true });
        return () => { clearTimeout(timer); savePosition(); editor.off('selectionUpdate', schedule); scroller?.removeEventListener('scroll', schedule); };
    }, [editor, positionScope]);

    // Zet de editor-inhoud vanuit markdown en houd bij welke bron de editor
    // weerspiegelt. Gebruik `preserveSelection` wanneer de gebruiker mogelijk in
    // de editor staat, zodat de caret niet naar het einde springt.
    const applyEditorSourceContent = useCallback((
        sourceContent: string,
        options?: { preserveSelection?: boolean }
    ) => {
        if (!editor) {
            return;
        }

        editor.view.dispatch(reviewTransaction(editor.state.tr, { clear: true }));
        editorSourceContentRef.current = sourceContent;
        const html = markdownToHtml(sourceContent);

        if (options?.preserveSelection) {
            setEditorContentPreservingSelection(editor, html);
        } else {
            editor.chain().setContent(html, false).setMeta('addToHistory', false).run();
        }
    }, [editor]);

    // Toon de context-highlight op de (nog in ProseMirror-state aanwezige)
    // selectie zodra de prompt-textarea de focus overneemt van de editor.
    const showSelectionContextHighlight = useCallback(() => {
        if (!editor) return;
        const { from, to, empty } = editor.state.selection;
        if (empty) return;
        editor.view.dispatch(editor.state.tr.setMeta(selectionContextPluginKey, { from, to }));
    }, [editor]);

    const clearSelectionContextHighlight = useCallback(() => {
        if (!editor) return;
        const decorations = selectionContextPluginKey.getState(editor.state);
        if (!decorations || decorations.find().length === 0) return;
        editor.view.dispatch(editor.state.tr.setMeta(selectionContextPluginKey, { clear: true }));
    }, [editor]);

    // Prompt-bar dicht = context weg; ruim de highlight dan ook op.
    useEffect(() => {
        if (!showPromptBar) {
            clearSelectionContextHighlight();
        }
    }, [showPromptBar, clearSelectionContextHighlight]);

    const getCurrentEditorSourceContent = useCallback(() => {
        saveQueue.materialize();
        if (!editor) {
            return summarySourceContent;
        }

        // Compose using the authoritative clean content ref (in sync with
        // assignment line numbers), not turndown output which may reformat lines.
        return composeEditorSourceContent(
            currentCleanContentRef.current,
            fieldAssignmentsRef.current
        );
    }, [editor, summarySourceContent, composeEditorSourceContent, saveQueue]);

    const serializeStreamDocument = (document: ProseMirrorNode, source: string): string => {
        const container = window.document.createElement('div');
        container.appendChild(DOMSerializer.fromSchema(document.type.schema).serializeFragment(document.content));
        measuredColWidthsRef.current = measureColWidthTokens({ state: { doc: document }, view: editor && !editor.isDestroyed ? editor.view : undefined });
        colWidthTableCounterRef.current = 0;
        const clean = turndownService.turndown(container.innerHTML);
        const parsed = parseStructuredFieldMarkdown(source);
        return composeStructuredFieldMarkdown(clean, normalizeAssignments(
            adjustAssignmentsForContentChange(parsed.cleanContent, clean, parsed.assignments), clean.split('\n').length));
    };
    const beginStreamEdit = (source: string, preserveExistingContent = false) => {
        if (!editor) return;
        if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);
        setEditorInitializingState(false);
        completedStreamRef.current = false;
        streamSourceRef.current = source;
        editor.view.dispatch(reviewTransaction(editor.state.tr, { clear: true }));
        streamEditRef.current = createSummaryStreamEdit(editor, preserveExistingContent);
        saveQueue.beginStream();
        editor.setEditable(canEdit && isEditing);
    };
    const applyStreamSource = (source: string, returnedTarget?: string) => {
        saveQueue.materialize();
        const edit = streamEditRef.current;
        if (!edit || !editor || editor.isDestroyed) return;
        edit.preview(markdownToHtml(source), extractColWidthTokensByTable(source), returnedTarget === undefined ? undefined : markdownToHtml(returnedTarget));
        const merged = edit.hasUserChanges ? serializeStreamDocument(editor.state.doc, source) : source;
        syncStructuredContentFromSource(merged);
        editorSourceContentRef.current = merged;
    };
    const completeStreamEdit = (serverSource: string) => {
        const edit = streamEditRef.current;
        if (!edit || !editor) return serverSource;
        applyStreamSource(serverSource);
        const merged = edit.hasUserChanges ? serializeStreamDocument(editor.state.doc, serverSource) : serverSource;
        edit.commit();
        streamEditRef.current = null;
        completedStreamRef.current = true;
        saveQueue.endStream();
        saveQueue.adopt(serverSource);
        syncStructuredContentFromSource(merged);
        editorSourceContentRef.current = merged;
        rememberSelfPushedSourceContent(merged);
        if (merged !== serverSource) saveQueue.stage(merged, editor.getJSON());
        return merged;
    };
    const rollbackStreamEdit = () => {
        saveQueue.materialize();
        const edit = streamEditRef.current;
        if (!edit || !editor || editor.isDestroyed) return;
        const checkpoint = edit.hasUserChanges ? edit.captureCheckpoint()() : undefined;
        edit.rollback();
        const recovery = edit.hasUserChanges ? serializeStreamDocument(editor.state.doc, streamSourceRef.current) : streamSourceRef.current;
        streamEditRef.current = null;
        syncStructuredContentFromSource(recovery);
        editorSourceContentRef.current = recovery;
        // A failed/disconnected stream may still save on the server. Require a
        // comparison before uploading this recovered draft over that result.
        saveQueue.suspended = false;
        saveQueue.conflict = true;
        saveQueue.stage(recovery, editor.getJSON(), checkpoint);
    };
    const recoverDraftConflict = async () => {
        if (!editor || !canEdit) return;
        const draft = saveQueue.draft;
        try {
            if (draft?.uncertain && await apiService.getSummaryStatus(summary.fileId, summary) === 'in_progress') {
                saveBlockReasonRef.current = t('summaryEditor.errors.aiBusy');
                return;
            }
            const server = await apiService.getSummary(summary.fileId, summary);
            if (!server) throw new Error('Cannot verify saved report');
            if (!mountedRef.current || queueRef.current !== saveQueue || saveQueue.suspended) return;
            saveQueue.materialize();
            // Typing or another tab may have changed the draft during the read.
            if (saveQueue.draft !== draft || saveQueue.storageConflict) return;
            const source = getSummarySourceContent(server);
            setConflictServer(source);
            let merged: string;
            if (draft?.stream) {
                const previous = editor.state.doc;
                try {
                    editor.commands.setContent(draft.stream.document, false);
                    streamEditRef.current = createSummaryStreamEdit(editor, false, draft.stream);
                    merged = completeStreamEdit(source);
                } catch {
                    streamEditRef.current = null;
                    editor.commands.setContent(previous.toJSON(), false);
                    return;
                }
            } else {
                const result = mergeSummaryDraft(draft?.base ?? saveQueue.confirmed, draft?.content ?? saveQueue.confirmed, source);
                if (result === null) return;
                merged = result;
                syncStructuredContentFromSource(merged);
                applyEditorSourceContent(merged, { preserveSelection: true });
                const initializing = isInitializingEditorRef.current;
                isInitializingEditorRef.current = true;
                try { hydrateColumnWidths(extractColWidthTokensByTable(merged)); }
                finally { isInitializingEditorRef.current = initializing; }
            }
            saveQueue.rebase(source, merged, editor.getJSON());
            lastSavedContentRef.current = source;
            rememberSelfPushedSourceContent(merged);
            setConflictServer(null); setDraftNotice(false); setPromptError('');
            onUpdate({ ...server, sourceContent: merged, content: parseStructuredFieldMarkdown(merged).cleanContent });
        } catch {
            // Keep the draft and comparison available if verification is unavailable.
            saveBlockReasonRef.current = t('summaryEditor.update.draftCheckFailed');
        }
    };
    const resolveDraftConflict = async (keepLocal: boolean): Promise<'resolved' | 'updated' | 'failed'> => {
        if (isSummaryBusy || !canEdit || !saveQueue.conflict) return 'failed';
        const server = await apiService.getSummary(summary.fileId, summary).catch(() => null);
        if (!mountedRef.current || queueRef.current !== saveQueue || saveQueue.suspended) return 'failed';
        if (!server) return 'failed';
        const source = getSummarySourceContent(server);
        // Refresh the comparison if another version arrived while it was open.
        if ((conflictServer ?? summarySourceContent) !== source) { setConflictServer(source); return 'updated'; }
        saveQueue.resolve(source, keepLocal);
        if (!keepLocal) {
            syncStructuredContentFromSource(source);
            applyEditorSourceContent(source, { preserveSelection: true });
            rememberSelfPushedSourceContent(source);
            onUpdate(server);
        }
        setConflictServer(null); setDraftNotice(false);
        if (keepLocal && !await flushManualSave()) return 'failed';
        setPromptError(''); setSaveError(false);
        return 'resolved';
    };

    useEffect(() => {
        if (editor) {
            editor.setEditable(canEdit && isEditing && saveQueue.loaded && (!isSummaryBusy || !!streamEditRef.current) && !showFieldOverlay);
        }
    }, [editor, canEdit, isEditing, isSummaryBusy, isStreaming, showFieldOverlay, saveQueue.loaded]);

    // Hydrateer de pixel-kolombreedtes in Tiptap's eigen model op basis van de
    // opgeslagen colwidth-comment (% → px t.o.v. de gerenderde tabelbreedte).
    // Vanaf dat moment beheert Tiptap de breedtes natief; bij opslaan worden ze
    // weer naar % gemeten (measureColWidthTokens).
    const hydrateColumnWidths = useCallback((tokensByTable: (string | null)[][]) => {
        if (!editor) return;
        hydrateColumnWidthsInEditor(editor, tokensByTable);
    }, [editor]);

    // Resync editor content when returning from the field overlay
    const prevShowFieldOverlayRef = useRef(showFieldOverlay);
    useEffect(() => {
        const wasShowingOverlay = prevShowFieldOverlayRef.current;
        prevShowFieldOverlayRef.current = showFieldOverlay;

        if (wasShowingOverlay && !showFieldOverlay && editor && !isInitializingEditorRef.current) {
            const sourceContent = composeEditorSourceContent();
            syncStructuredContentFromSource(sourceContent);
            const colWidthTokens = extractColWidthTokensByTable(sourceContent);
            setEditorInitializingState(true);
            applyEditorSourceContent(sourceContent, { preserveSelection: true });
            setTimeout(() => {
                hydrateColumnWidths(colWidthTokens);
                setEditorInitializingState(false);
            }, 200);
        }
    }, [showFieldOverlay, editor, composeEditorSourceContent, setEditorInitializingState, syncStructuredContentFromSource, hydrateColumnWidths, applyEditorSourceContent]);

    useEffect(() => {
        if (editor) {
            if (generation.isBlocked() || activeSummaryUpdateRef.current || streamEditRef.current || !saveQueue.loaded) return;
            const isFirstInitialization = !hasInitializedEditorContentRef.current;

            // Alleen een echte externe wijziging mag het document vervangen.
            // Zonder deze check reset de echo van onze eigen autosave de editor
            // en springt de caret naar het einde van het verslag.
            if (!isFirstInitialization && (saveQueue.pending || isEchoOfEditorContent(summarySourceContent))) {
                return;
            }

            // Clear any existing initialization timeout
            if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
            }

            setEditorInitializingState(true);
            hasInitializedEditorContentRef.current = true;

            // Convert markdown content to HTML and set in editor
            const initialSource = isFirstInitialization && canEdit ? saveQueue.content : summarySourceContent;
            const parsedContent = syncStructuredContentFromSource(initialSource);
            const colWidthTokens = extractColWidthTokensByTable(parsedContent.cleanContent);
            applyEditorSourceContent(initialSource, { preserveSelection: !isFirstInitialization });
            if (isFirstInitialization && canEdit && saveQueue.document) {
                try { editor.chain().setContent(saveQueue.document, false).setMeta('addToHistory', false).run(); }
                catch { /* Markdown remains recoverable if an older document schema differs. */ }
            }

            // Initialize history with current content if it's the first time
            if (history.length === 0) {
                // console.log('🔄 HISTORY: Initializing history with first entry', {
                //     contentLength: (summary.content || '').length,
                //     summaryHasContent: !!(summary.content && summary.content.trim())
                // });

                setHistory([{
                    content: initialSource || '',
                    timestamp: new Date().toISOString(),
                    type: 'initial',
                    description: 'Originele inhoud'
                }]);
                setHistoryIndex(0);

                // console.log('🔄 HISTORY: History initialized with initial entry');
            } else {
                // console.log('🔄 HISTORY: History already exists, not reinitializing', {
                //     historyLength: history.length,
                //     currentIndex: historyIndex
                // });
            }

            // Allow a brief moment for the editor to settle (layout settled, so
            // offsetWidth is valid), hydrate the column widths from %, then
            // enable change tracking. Hydration runs while still initializing so
            // its transaction does not trigger an autosave.
            initTimeoutRef.current = setTimeout(() => {
                hydrateColumnWidths(colWidthTokens);
                if (isFirstInitialization && restoredPosition && editor) {
                    editor.view.dispatch(editor.state.tr.setSelection(summaryPositionSelection(editor.state.doc, restoredPosition)).setMeta('addToHistory', false));
                    const scroller = editor.view.dom.closest('main');
                    if (scroller) scroller.scrollTop = restoredPosition.scrollTop;
                }
                setEditorInitializingState(false);
                // Alleen bij de eerste initialisatie de focus zetten (forceert een
                // re-render zodat attrs bewaard blijven). Bij een latere externe
                // update zou dit de caret uit de handen van de gebruiker trekken.
                if (isFirstInitialization) {
                    editor.commands.focus();
                }
            }, 200);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- history.length bewust weggelaten: opnieuw draaien tijdens het typen zou de editorinhoud terugzetten
    }, [saveQueue.loaded, editor, setEditorInitializingState, summarySourceContent, syncStructuredContentFromSource, hydrateColumnWidths, applyEditorSourceContent, isEchoOfEditorContent]);

    useEffect(() => {
        if (editor) {
            editor.setEditable(canEdit && isEditing && saveQueue.loaded && (!isSummaryBusy || !!streamEditRef.current) && !showFieldOverlay);
            // During AI streaming we keep the editor blurred so the browser does not
            // force-scroll to the caret position on every streamed chunk.
            if (isStreaming && editor.isFocused) {
                editor.commands.blur();
            }
        }
    }, [editor, canEdit, isEditing, isSummaryBusy, isStreaming, showFieldOverlay, saveQueue.loaded]);

    // Keyboard shortcuts for undo/redo
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const target = e.target instanceof HTMLElement ? e.target : null;
            const isTypingInNonSummaryField = isEditableField(target) && !isSummaryEditorField(target);

            if (e.defaultPrevented || isTypingInNonSummaryField) {
                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                performUndo();
            } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                e.preventDefault();
                performRedo();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- performUndo/performRedo zijn geen useCallback; de listener wordt al ververst zodra canUndo/canRedo wijzigen
    }, [canUndo, canRedo]);

    // Close options menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Check if the click was outside the options menu
            const target = event.target as Element;
            if (showOptionsMenu && !target.closest('.options-menu-container')) {
                setShowOptionsMenu(false);
            }
        };

        if (showOptionsMenu) {
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }
    }, [showOptionsMenu]);

    // Close delete confirmation on Escape key
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && showDeleteConfirm && !deletingRef.current) {
                setShowDeleteConfirm(false);
            }
        };

        if (showDeleteConfirm) {
            document.addEventListener('keydown', handleEscapeKey);
            return () => document.removeEventListener('keydown', handleEscapeKey);
        }
    }, [showDeleteConfirm]);

    // Cleanup timeouts on unmount
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            streamEditRef.current = null;
            if (toolbarFrameRef.current !== undefined) cancelAnimationFrame(toolbarFrameRef.current);
            toolbarFrameRef.current = undefined;
            changeScrollRef.current?.cancel();
            activeSummaryUpdateRef.current?.previews.finish();
            activeSummaryUpdateRef.current?.measurement.cancelPaints();
            activeSummaryUpdateRef.current = null;
            lastSummaryUpdateIdRef.current = null;
            if (saveToHistoryTimeoutRef.current) {
                clearTimeout(saveToHistoryTimeoutRef.current);
            }
            if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
            }
            clearStreamStatusSequence();
        };
    }, [clearStreamStatusSequence]);

    const generationRunningRef = useRef(false);
    const runGeneration = async () => {
        if (!editor || generation.isBlocked() || generationRunningRef.current || activeSummaryUpdateRef.current || isReadOnly || !saveQueue.loaded) return;
        generationRunningRef.current = true;
        const abortController = new AbortController();
        setStreamAbortController(abortController);
        setUpdateVersion(null); setUpdateMessage(''); setShowOptionsMenu(false);
        setIsStreaming(true); setShowDiff(false); lastAiChangeRef.current = null;
        setError(''); setCanRetry(false);
        startGenericStreamStatus(t('summaryEditor.streamStatus.regeneratingReport'));
        let completed = false;
        let started = false;
        try {
            await withSummaryUpdateLock(JSON.stringify([getDomain(), user?.id, summary.fileId, summary.meetingType]), async () => {
                let source = getCurrentEditorSourceContent();
                saveQueue.stage(source, editor.getJSON());
                if (!await flushManualSave()) throw new Error(t('summaryEditor.update.saveFirstFailed'));
                if (!mountedRef.current || abortController.signal.aborted) return;
                source = getCurrentEditorSourceContent();
                beginStreamEdit(source); started = true;
                let contentBuffer = source;
                let currentTitle = summary.title;
                const recordingsToUse = summaryAdditionalRecordings.length > 0 ? summaryAdditionalRecordings : additionalRecordings;
                const ids = recordingsToUse.map(item => item.id);
                const stream = apiService.streamSummaryWithPrompt(summary.fileId, summary,
                    'Maak het verslag opnieuw', undefined, true, source, ids);
                for await (const update of stream) {
                    // The server may continue after stop/navigation. Drain to terminal
                    // while retaining the same document lock and durable recovery draft.
                    if (!mountedRef.current || abortController.signal.aborted) continue;
                    if (update.pendingTarget) streamEditRef.current?.lockTarget(markdownToHtml(update.pendingTarget));
                    if (update.title) {
                        currentTitle = update.title;
                        if (!recordingTitleUpdatedRef.current && recording.name === 'Naamloze opname' && currentTitle.trim()) {
                            recordingTitleUpdatedRef.current = true; onRecordingTitleUpdate?.(currentTitle.trim());
                        }
                    }
                    if (update.content !== undefined) {
                        contentBuffer = update.content;
                        markStreamContentUpdating(); applyStreamSource(contentBuffer);
                    }
                    if (!update.isComplete) continue;
                    const refreshed = await apiService.getSummary(summary.fileId, summary).catch(() => null);
                    if (!mountedRef.current || abortController.signal.aborted) continue;
                    const serverSource = refreshed ? getSummarySourceContent(refreshed) : contentBuffer;
                    const merged = completeStreamEdit(serverSource);
                    onUpdate({ ...summary, title: currentTitle, ...refreshed, sourceContent: merged,
                        content: parseStructuredFieldMarkdown(merged).cleanContent, updatedAt: new Date().toISOString() });
                    addToHistory(merged, 'ai_improvement', 'Verslag opnieuw gegenereerd');
                    completed = true;
                    if (saveQueue.pending) await flushManualSave();
                    break;
                }
                if (!completed && mountedRef.current && !abortController.signal.aborted) throw new SummaryUpdateInterruptedError();
            });
        } catch (failure) {
            if (mountedRef.current && !abortController.signal.aborted) {
                setError(failure instanceof Error ? failure.message : t('summaryEditor.errors.createSummary'));
                setCanRetry(!started);
            }
        } finally {
            generationRunningRef.current = false;
            if (mountedRef.current) {
                if (!completed && started && streamEditRef.current) rollbackStreamEdit();
                setIsStreaming(false); setStreamAbortController(null);
            }
        }
    };
    const startInitialGeneration = (explicitRetry = false) => {
        const preferences = getSummaryUpdatePreferences(user, summaryDomain);
        const recordingsToUse = summaryAdditionalRecordings.length ? summaryAdditionalRecordings : additionalRecordings;
        const ids = recordingsToUse.length ? recordingsToUse.map(item => item.id) : summary.additionalRecordings ?? [];
        return generation.start(ids, { platform: 'default', llmModel: preferences.llmModel,
            summaryGenerationVersion: isFrontendFeatureEnabled(user, 'summary_generation_v3') ? 'v3' : 'v2',
            templateRevisionId: summary.templateRevisionId }, explicitRetry);
    };
    useEffect(() => {
        if (!editor || isReadOnly || !saveQueue.loaded || isStreaming) return;
        const key = `${summary.fileId}-${summary.meetingType}`;
        if (streamingStartedRef.current === key) return;
        if (!generation.hasAttempt() && (saveQueue.restored || summarySourceContent.trim())) return;
        streamingStartedRef.current = key;
        void startInitialGeneration();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one start/recovery per report after draft loading
    }, [editor, summary.fileId, summary.meetingType, isReadOnly, saveQueue.loaded]);

    const handleDeleteSummary = async () => {
        if (isSummaryBusy || streamEditRef.current || deletingRef.current || !canEdit) return;
        deletingRef.current = true;
        setIsDeleting(true); setDeleteError('');
        let deleted = false;
        let paused = false;
        try {
            await withSummaryUpdateLock(JSON.stringify([getDomain(), user?.id, summary.fileId, summary.meetingType]), async () => {
                // Deletion supersedes unsaved edits. Only wait for a save already sent.
                paused = true;
                await saveQueue.pause();
                if (!mountedRef.current || queueRef.current !== saveQueue) return;
                if (!await apiService.deleteSummary(summary.fileId, summary)) throw new Error(t('summaryEditor.errors.deleteSummary'));
                deleted = true;
                await saveQueue.discard();
                if (!mountedRef.current || queueRef.current !== saveQueue) return;
                setShowDeleteConfirm(false); setShowOptionsMenu(false);
                onDelete?.(summary); onBack();
            });
        } catch (error) {
            logger.error('Error deleting summary:', error);
            if (mountedRef.current) setDeleteError(t('summaryEditor.errors.deleteSummary'));
        } finally {
            if (!deleted) {
                if (paused) saveQueue.resume();
                deletingRef.current = false;
                if (mountedRef.current) setIsDeleting(false);
            }
        }
    };

    const confirmDelete = () => {
        setShowOptionsMenu(false);
        setDeleteError('');
        setShowDeleteConfirm(true);
    };

    const handleRegenerateRequest = () => runGeneration();

    const handlePromptSubmit = async (promptText: string) => {
        if (!promptText.trim() || isReadOnly || isSummaryBusy || generation.isBlocked() || generationRunningRef.current || activeSummaryUpdateRef.current) return;

        lastImprovementPromptRef.current = promptText;
        changeScrollRef.current?.cancel();
        changeScrollRef.current = createSummaryChangeScroll(() => editor?.view.dom);
        // Capture every request choice at the click, before any asynchronous save.
        const preferences = getSummaryUpdatePreferences(user, getDomain());
        const measurement = new SummaryUpdateMeasurement(preferences);
        const request = {
            id: measurement.requestId,
            terminal: false,
            metadata: {} as Record<string, unknown>,
            recovery: lastSavedContentRef.current,
            measurement,
            previews: new SummaryPreviewScheduler((source) => {
                if (activeSummaryUpdateRef.current !== request) return;
                flushSync(() => {
                    applyStreamSource(source);
                    if (editor) {
                        const baseline = streamEditRef.current?.hasUserChanges ? streamEditRef.current.recoveryDocument
                            : createDocument(markdownToHtml(comparisonBaseline), editor.schema);
                        editor.view.dispatch(reviewTransaction(editor.state.tr, {
                            restore: { ...summarySnapshotReview(baseline, editor.state.doc, true), active: true },
                        }));
                    }
                    setHasStreamPreview(true);
                });
                changeScrollRef.current?.reveal();
                measurement.rendered(isChangedReportVisible, !request.terminal);
            }),
        };
        activeSummaryUpdateRef.current = request;
        lastSummaryUpdateIdRef.current = request.id;
        const isCurrent = () => activeSummaryUpdateRef.current === request;
        const originalSummary = { ...summary };
        const selectedMarkdown = selectedText;
        const selectedRange = editor && !editor.state.selection.empty ? { from: editor.state.selection.from, to: editor.state.selection.to } : null;
        let currentContent = getCurrentEditorSourceContent();
        let comparisonBaseline = currentContent;
        const recordingsToUse = summaryAdditionalRecordings.length > 0 ? summaryAdditionalRecordings : additionalRecordings;
        const additionalIds = recordingsToUse.map(item => item.id);
        let requestSent = false;
        let currentTitle = originalSummary.title;
        const applyUpdateSource = (source: string, target?: string) => { applyStreamSource(source, target); };

        function isChangedReportVisible() {
            if (!mountedRef.current || lastSummaryUpdateIdRef.current !== request.id || document.visibilityState !== 'visible') return false;
            const element = editor?.view.dom;
            const visibleSource = editorSourceContentRef.current;
            if (!element || visibleSource === null || normalizeMarkdownContent(visibleSource) === normalizeMarkdownContent(request.recovery)) return false;
            const rect = element.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0;
        }

        // Ref-based guards also cover a second click before React has rendered.
        saveQueue.cancelTimers();
        if (saveToHistoryTimeoutRef.current) clearTimeout(saveToHistoryTimeoutRef.current);
        saveToHistoryTimeoutRef.current = null;
        editor?.setEditable(false);
        flushSync(() => {
            setIsStreaming(true);
            setUpdateVersion(preferences.summaryUpdateVersion);
            setUpdateMessage('');
            setHasStreamPreview(false);
            setShowFieldOverlay(false);
            setActiveTab('summary');
            setPromptError('');
            resetStreamStatus(t('summaryEditor.update.preparing'));
            setError('');
            setCanRetry(false);
            setShowDiff(false);
        });

        try {
            await withSummaryUpdateLock(JSON.stringify([getDomain(), user?.id, summary.fileId, summary.meetingType]), async () => {
                // Await the entire manual save, including its local state update.
                if (!await flushManualSave()) throw new Error(saveBlockReasonRef.current ?? t(saveQueue.conflict ? 'summaryEditor.update.resolveDraftFirst' : 'summaryEditor.update.saveFirstFailed'));
                if (!isCurrent()) return;
                currentContent = getCurrentEditorSourceContent();
                if (currentContent !== saveQueue.confirmed) {
                    saveQueue.stage(currentContent);
                    if (!await flushManualSave()) throw new Error(saveBlockReasonRef.current ?? t(saveQueue.conflict ? 'summaryEditor.update.resolveDraftFirst' : 'summaryEditor.update.saveFirstFailed'));
                    if (!isCurrent()) return;
                }
                currentContent = getCurrentEditorSourceContent();
                comparisonBaseline = currentContent;
                lastAiSnapshotRef.current = editor ? { source: currentContent, document: editor.getJSON() } : null;
                request.recovery = currentContent;
                const currentSelectedRange = selectedRange && editor && !editor.state.selection.empty
                    ? { from: editor.state.selection.from, to: editor.state.selection.to } : null;
                beginStreamEdit(currentContent);
                streamEditRef.current?.startReview(preferences.summaryUpdateVersion === 'v2' && currentSelectedRange ? currentSelectedRange : { from: 0, to: 0 }, selectedRange ? 'selection' : 'document');
                setReviewBlocked(false);
                // Only consume the prompt once the save checks allow the AI request.
                clearSelectionContextHighlight();
                setShowPromptBar(false);
                setPromptValue('');
                requestSent = true;
                const stream = apiService.streamSummaryWithPrompt(
                    originalSummary.fileId, originalSummary, promptText,
                    selectedMarkdown, false, currentContent, additionalIds, preferences,
                );
                for await (const update of stream) {
                    measurement.metadata(update.metadata);
                    request.metadata = { ...request.metadata, ...update.metadata };
                    if (update.event === 'preview' || (!update.isComplete && update.content !== undefined)) measurement.receivedPreview();
                    // Keep draining a detached stream to retain the document lock.
                    // Navigating away cannot cancel the server task.
                    if (!isCurrent()) {
                        if (update.isComplete) measurement.finish('success');
                        continue;
                    }
                    if (update.event === 'source' && typeof update.metadata?.original_summary === 'string') {
                        comparisonBaseline = update.metadata.original_summary;
                    }
                    if (update.pendingTarget) streamEditRef.current?.lockTarget(markdownToHtml(update.pendingTarget));
                    if (update.title) {
                        currentTitle = update.title;
                        if (!recordingTitleUpdatedRef.current && recording.name === 'Naamloze opname' && onRecordingTitleUpdate && currentTitle.trim()) {
                            recordingTitleUpdatedRef.current = true;
                            onRecordingTitleUpdate(currentTitle.trim());
                        }
                    }
                    if (update.event === 'status' && update.phase && ['queued', 'preparing', 'generating', 'validating', 'saving'].includes(update.phase)) {
                        resetStreamStatus(t(`summaryEditor.update.phases.${update.phase}`));
                    }
                    if (update.event === 'preview' && update.content !== undefined) {
                        request.previews.preview(update.content);
                    } else if (preferences.summaryUpdateVersion === 'v2' && update.content !== undefined && !update.isComplete) {
                        // V2 still uses the existing XML/text interpretation and editor updates.
                        resetStreamStatus(t('summaryEditor.update.phases.generating'));
                        applyUpdateSource(update.content, update.releasedTarget);
                        measurement.rendered(isChangedReportVisible, true);
                    }
                    if (!update.isComplete) continue;

                    // V3's service only emits isComplete for a valid result.
                    request.terminal = true;
                    request.previews.finish();
                    measurement.cancelPaints();
                    const finalSource = update.content ?? currentContent;
                    const noChange = update.result?.mode === 'no_change' && update.result.saved === false;
                    const hasChanges = !noChange && normalizeMarkdownContent(comparisonBaseline) !== normalizeMarkdownContent(finalSource);
                    const hadUserChanges = () => !!streamEditRef.current?.hasUserChanges;
                    flushSync(() => {
                        applyUpdateSource(finalSource);
                        streamEditRef.current?.releaseLocks();
                        setReviewBlocked(false);
                        setHasStreamPreview(false);
                        setUpdateMessage(t(noChange ? 'summaryEditor.update.noChange' : 'summaryEditor.update.saved'));
                        setIsStreaming(false);
                        setIsRefreshingSummary(true);
                    });
                    measurement.rendered(isChangedReportVisible, false);
                    measurement.finish('success');
                    // Keep the merge session alive through refresh; typing may continue.
                    const refreshed = await apiService.getSummary(originalSummary.fileId, originalSummary).catch(() => null);
                    if (!isCurrent()) return;
                    const serverSource = refreshed ? getSummarySourceContent(refreshed) : finalSource;
                    const reviewBaseline = hadUserChanges() ? streamEditRef.current?.recoveryDocument
                        : lastAiSnapshotRef.current?.source === comparisonBaseline ? editor?.schema.nodeFromJSON(lastAiSnapshotRef.current.document)
                        : editor ? createDocument(markdownToHtml(comparisonBaseline), editor.schema) : undefined;
                    const merged = completeStreamEdit(serverSource);
                    lastSavedContentRef.current = serverSource;
                    lastAiChangeRef.current = hasChanges ? { beforeContent: comparisonBaseline, afterContent: merged } : null;
                    if (editor && reviewBaseline) editor.view.dispatch(reviewTransaction(editor.state.tr, {
                        restore: hasChanges ? summarySnapshotReview(reviewBaseline, editor.state.doc) : emptySummaryReview(),
                    }));
                    setShowDiff(hasChanges);
                    changeScrollRef.current?.reveal();
                    onUpdate({ ...originalSummary, title: currentTitle, ...refreshed, sourceContent: merged,
                        content: parseStructuredFieldMarkdown(merged).cleanContent, updatedAt: new Date().toISOString() });
                    addToHistory(merged, 'ai_improvement', `AI verbetering: ${promptText.slice(0, 50)}`);
                    if (!refreshed) setUpdateMessage(t(noChange ? 'summaryEditor.update.noChangeRefreshFailed' : 'summaryEditor.update.savedRefreshFailed'));
                    if (saveQueue.pending) await flushManualSave();
                    break;
                }
            });
        } catch (failure) {
            // A result stays successful even if a subsequent local refresh fails.
            if (request.terminal) {
                if (isCurrent()) setUpdateMessage(t('summaryEditor.update.savedRefreshFailed'));
            } else {
                changeScrollRef.current?.cancel();
                if (failure instanceof SummaryUpdateError) measurement.metadata(failure.metadata);
                measurement.finish(failure instanceof SummaryUpdateInterruptedError ? 'interrupted' : 'error');
                request.previews.finish();
                measurement.cancelPaints();
                if (isCurrent()) {
                    setHasStreamPreview(false);
                    if (requestSent) rollbackStreamEdit();
                    const message = failure instanceof Error ? failure.message : t('summaryEditor.errors.improveSummary');
                    if (requestSent) {
                        setError(message);
                        setCanRetry(preferences.summaryUpdateVersion === 'v2');
                    } else {
                        setPromptError(message);
                        setPromptValue(promptText);
                        setShowPromptBar(true);
                    }
                }
                if (requestSent) {
                    const refreshed = await apiService.getSummary(originalSummary.fileId, originalSummary).catch(() => null);
                    if (isCurrent() && refreshed) {
                        const source = getSummarySourceContent(refreshed);
                        if (saveQueue.content === request.recovery) {
                            saveQueue.endStream(); saveQueue.adopt(source);
                            syncStructuredContentFromSource(source);
                            applyEditorSourceContent(source, { preserveSelection: true });
                            lastSavedContentRef.current = source;
                            rememberSelfPushedSourceContent(source);
                            onUpdate(refreshed);
                        } else setConflictServer(source);
                    }
                }
            }
        } finally {
            if (isCurrent()) {
                activeSummaryUpdateRef.current = null;
                setIsStreaming(false);
                setIsRefreshingSummary(false);
                if (requestSent) setSelectedText('');
            }
        }
    };

    const handleChatAddToSummary = async (messageContent: string) => {
        if (!messageContent.trim()) {
            return;
        }

        if (isSummaryBusy || activeSummaryUpdateRef.current) {
            throw new Error(t('summaryEditor.errors.aiBusy'));
        }

        const prompt = `Verwerk deze informatie in de samenvatting op een logische plek: ${messageContent}`;
        await handlePromptSubmit(prompt);
    };

    // Functie om vorige AI versie te halen (alleen als er een AI wijziging was)
    const getPreviousHistoryContent = (): string | null => {
        // Return the last AI change's before content if available
        return lastAiChangeRef.current?.beforeContent || null;
    };

    // Functie om diff view te activeren (alleen aanzetten)
    const activateDiffView = () => {
        if (!editor || isSummaryBusy) return;

        const previousContent = getPreviousHistoryContent();
        if (!previousContent) return;

        // Toon diff: pas marks toe met vorige versie uit history
        saveQueue.materialize();
        editor.view.dispatch(reviewTransaction(editor.state.tr, {
            restore: summarySnapshotReview(createDocument(markdownToHtml(previousContent), editor.schema), editor.state.doc),
        }));
        setShowDiff(true);
    };

    const toggleDiffView = () => {
        if (!editor || isSummaryBusy) return;

        const previousContent = getPreviousHistoryContent();
        if (!previousContent) return;

        const review = summaryReviewKey.getState(editor.state);
        if (review?.visible) {
            editor.view.dispatch(reviewTransaction(editor.state.tr, { highlighted: !showDiff }));
            setShowDiff(!showDiff);
        } else activateDiffView();
    };

    const navigateAiChange = (direction: number) => {
        if (!editor || isSummaryBusy) return;
        const review = summaryReviewKey.getState(editor.state);
        if (!review) return;
        const groups = summaryReviewGroups(editor.state.doc, review);
        if (!groups.length) return;
        const current = groups.findIndex(group => group.changeIds.includes(review.selected ?? -1));
        const index = current < 0 ? (direction > 0 ? 0 : groups.length - 1) : (current + direction + groups.length) % groups.length;
        const group = groups[index];
        changeScrollRef.current?.cancel();
        editor.view.dispatch(reviewTransaction(editor.state.tr, { highlighted: true }));
        editor.view.dispatch(reviewTransaction(editor.state.tr.setSelection(Selection.near(editor.state.doc.resolve(group.from))), { select: group.id }));
        setShowDiff(true);
        editor.view.dom.querySelector(`[data-review-change="${group.id}"]`)?.scrollIntoView?.({
            block: 'center', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
    };

    const keepAiChanges = () => {
        if (!editor || isSummaryBusy) return;
        editor.view.dispatch(reviewTransaction(editor.state.tr, { clear: true }));
        lastAiChangeRef.current = null;
        lastAiSnapshotRef.current = null;
        setShowDiff(false);
        setUpdateMessage('');
    };
    const restoreBeforeAiChanges = () => {
        if (!editor || isSummaryBusy || !lastAiChangeRef.current) return;
        const snapshot = lastAiSnapshotRef.current;
        const source = snapshot?.source ?? lastAiChangeRef.current.beforeContent;
        setEditorInitializingState(true);
        if (snapshot) editor.commands.setContent(snapshot.document);
        else applyEditorSourceContent(source, { preserveSelection: true });
        syncStructuredContentFromSource(source);
        editorSourceContentRef.current = source;
        editor.view.dispatch(reviewTransaction(editor.state.tr, { clear: true }));
        setEditorInitializingState(false);
        lastAiChangeRef.current = null;
        lastAiSnapshotRef.current = null;
        setShowDiff(false);
        setUpdateMessage('');
        addToHistory(source, 'user_edit', t('summaryEditor.changes.restore'));
        saveQueue.stage(source, editor?.getJSON());
        void flushManualSave();
    };

    const retryProcessing = async () => {
        if (canRetry) {
            setError('');
            setCanRetry(false);
            if (lastImprovementPromptRef.current) await handlePromptSubmit(lastImprovementPromptRef.current);
            else await handleRegenerateRequest();
        }
    };

    const getPromptSuggestions = () => {
        if (selectedText && selectedText.trim()) {
            return [
                t('summaryEditor.suggestions.selection.elaborate'),
                t('summaryEditor.suggestions.selection.summarizeShorter'),
                t('summaryEditor.suggestions.selection.examplesFromTranscript'),
                t('summaryEditor.suggestions.selection.simplerLanguage'),
                t('summaryEditor.suggestions.selection.relateToRest')
            ];
        } else {
            return [
                t('summaryEditor.suggestions.general.summarizeKeyPoints'),
                t('summaryEditor.suggestions.general.moreDetails'),
                t('summaryEditor.suggestions.general.simplerLanguage'),
                t('summaryEditor.suggestions.general.addConclusion'),
                t('summaryEditor.suggestions.general.highlightActionItems'),
            ];
        }
    };

    const clearError = () => {
        setError('');
        setCanRetry(false);
    };

    const openFieldOverlay = () => {
        if (!canEdit) {
            return;
        }

        if (editor) {
            editor.commands.blur();
        }

        // Flush pending typing once before opening fields, keeping marker line
        // assignments aligned with the latest editor document.
        syncStructuredContentFromSource(getCurrentEditorSourceContent());

        setIsEditing(true);
        setShowPromptBar(false);
        setShowFieldOverlay(true);
    };

    const stopStreaming = () => {
        if (activeSummaryUpdateRef.current) return;
        if (streamAbortController) {
            rollbackStreamEdit();
            streamAbortController.abort();
            setStreamAbortController(null);
        }
        setIsStreaming(false);
        resetStreamStatus();
        setError('');
        setCanRetry(false);
    };

    const navigateAfterSave = async (onNavigate: () => void) => {
        if (await saveQueue.settleLocal() || await flushManualSave()) {
            if (!saveQueue.suspended) void saveQueue.flush();
            onNavigate();
        }
    };

    if (!editor) {
        return (
            <div className="summary-editor">
                <PageHeader title={meetingTypeTitle ?? summary.meetingType} onBack={onBack}
                    breadcrumbs={[
                        { label: t('recordingsPage.title'), onClick: onBackToRecordings },
                        { label: recording.name, onClick: onBack },
                    ]} />
                <div className="editor-main-layout" aria-busy="true">
                    <div className="editor-content" />
                </div>
            </div>
        );
    }

    const review = summaryReviewKey.getState(editor.state) ?? emptySummaryReview();
    const reviewGroups = summaryReviewGroups(editor.state.doc, review);
    const showReview = review.visible || !!updateMessage || (isStreaming && !!updateVersion);
    const reviewStatus = isStreaming
        ? reviewBlocked ? t('summaryEditor.changes.blockedStatus')
            : hasStreamPreview && streamStatusText === t('summaryEditor.update.preparing')
                ? t('summaryEditor.changes.improving') : streamStatusText || t('summaryEditor.changes.improving')
        : updateMessage || t('summaryEditor.changes.completed');
    const reviewDescription = hasStreamPreview ? t('summaryEditor.changes.previewHint')
        : isStreaming ? t(reviewBlocked ? 'summaryEditor.changes.blocked' : 'summaryEditor.changes.lockHint')
        : isRefreshingSummary ? t('summaryEditor.update.refreshing')
        : reviewGroups.length ? t('summaryEditor.changes.completedHint') : t('summaryEditor.changes.emptyHint');
    const streamingStatus = generation.running
        ? t(`summaryEditor.generation.phases.${generation.phase}`)
        : streamStatusText || t('summaryEditor.streamingIndicator');

    return (
        <div className={`summary-editor ${isChatOpen ? 'summary-editor--chat-open' : ''} ${generation.streaming ? 'is-generating' : ''}`}>
            <PageHeader ref={editorHeaderRef} title={meetingTypeTitle}
                onBack={() => navigateAfterSave(onBack)}
                breadcrumbs={[
                    { label: t('recordingsPage.title'), onClick: () => navigateAfterSave(onBackToRecordings) },
                    { label: recording.name, onClick: () => navigateAfterSave(onBack) },
                ]}
                actions={<>
                    <AudioPlayer
                        ref={audioPlayerRef}
                        fileId={recording.id}
                        audioUrl={audioUrl || undefined}
                        onAudioLoaded={setAudioDuration}
                        onTimeUpdate={setCurrentAudioTime}
                        compact={true}
                    />
                    <button
                        type="button"
                        onClick={() => setShowTranscript(!showTranscript)}
                        className={`editor-header-toggle ${showTranscript ? 'is-active' : ''}`}
                        title={showTranscript ? t('summaryEditor.transcript.hide') : t('summaryEditor.transcript.show')}
                    >
                        <svg viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                    </button>
                    <div className="editor-status">
                        {!generation.running && !isStreaming && isSaving && (
                            <span className="status-saving"><span className="spinner-small" /> {t('common.saving')}</span>
                        )}
                        {!isSummaryBusy && !isSaving && canEdit && saveQueue.pending && saveQueue.durable && !saveQueue.conflict && (
                            <span className="status-local" role="status" title={t('summaryEditor.draft.savedLocally')}>{t('summaryEditor.draft.localStatus')}</span>
                        )}
                    </div>
                </>}
            />

            {canEdit && saveQueue.storageFailed && <div className="summary-save-error" role="alert">{t('summaryEditor.draft.storageFailed')}</div>}
            {canEdit && draftNotice && !saveQueue.conflict && <div className="summary-draft-status" role="status">{t('summaryEditor.draft.restored')}</div>}
            {canEdit && saveQueue.conflict && <SummaryDraftConflict
                key={JSON.stringify(positionScope)}
                reviewRequest={draftReviewRequest}
                localContent={saveQueue.content}
                serverContent={conflictServer ?? summarySourceContent}
                competingContent={saveQueue.competingDraft?.content}
                disabled={isSummaryBusy}
                onResolve={resolveDraftConflict}
            />}
            {saveError && <div className="summary-save-error" role="alert">
                <span>{t('summaryEditor.save.failed')}</span>
                <button type="button" onClick={() => { void flushManualSave(); }}>{t('summaryEditor.save.retry')}</button>
            </div>}

            {/* Transcript Panel */}
            {showTranscript && transcript && (
                <div style={{ background: 'var(--container)', borderBottom: '1px solid var(--border)' }}>
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <TranscriptViewer
                            transcript={transcript}
                            fileId={recording.id}
                            currentTime={currentAudioTime}
                            canDownload={canDownload}
                            onSeek={(time) => {
                                if (audioPlayerRef.current) {
                                    audioPlayerRef.current.seekTo(time);
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Compact Multiple Recordings Info */}
            {(summaryAdditionalRecordings.length > 0 || (summary.supportingDocuments && summary.supportingDocuments.length > 0)) && (
                <div className="bg-gray-50 border-b border-gray-200">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Compact Bar */}
                        <button
                            onClick={() => setShowAdditionalRecordings(!showAdditionalRecordings)}
                            className="w-full py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center space-x-3">
                                <svg className="w-4 h-4 text-primary-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <rect x="3" y="3" width="6" height="6" rx="1.5"></rect>
                                    <rect x="11" y="3" width="6" height="6" rx="1.5"></rect>
                                    <rect x="3" y="11" width="6" height="6" rx="1.5"></rect>
                                    <rect x="11" y="11" width="6" height="6" rx="1.5"></rect>
                                </svg>
                                <span className="text-sm font-medium text-gray-700">
                                    {summaryAdditionalRecordings.length > 0 ? (
                                        <>{t('summaryEditor.recordings.summaryOfCount', { count: summaryAdditionalRecordings.length + 1 })}</>
                                    ) : (
                                        <>{t('summaryEditor.recordings.summaryOfName', { name: recording.name })}</>
                                    )}
                                    {summary.supportingDocuments && summary.supportingDocuments.length > 0 && (
                                        <span className="text-gray-500"> • {t('summaryEditor.recordings.documentCount', { count: summary.supportingDocuments.length })}</span>
                                    )}
                                </span>
                                {summaryAdditionalRecordings.length > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                                        {summaryAdditionalRecordings.length + 1}
                                    </span>
                                )}
                                {summary.supportingDocuments && summary.supportingDocuments.length > 0 && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                        </svg>
                                        {summary.supportingDocuments.length}
                                    </span>
                                )}
                            </div>
                            <svg
                                className={`w-4 h-4 text-gray-400 transition-transform ${showAdditionalRecordings ? 'rotate-180' : ''}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>

                        {/* Expandable Details */}
                        {showAdditionalRecordings && (
                            <div className="pb-4">
                                <div className="bg-white rounded-lg border border-gray-200 p-4">
                                    <div className="space-y-3">
                                        {/* Hoofdopname */}
                                        <div className="flex items-center justify-between p-3 bg-primary-50 rounded-lg border border-primary-200">
                                            <div className="flex items-center space-x-3">
                                                <div className="flex-shrink-0">
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-600 text-white">
                                                        {t('summaryEditor.recordings.mainBadge')}
                                                    </span>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{recording.name}</p>
                                                    <p className="text-xs text-gray-500">
                                                        {recording.duration ? `${recording.duration} • ` : ''}{new Date(recording.createdAt).toLocaleDateString('nl-NL')}
                                                    </p>
                                                </div>
                                            </div>
                                            {recording.tags && recording.tags.length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {recording.tags.slice(0, 2).map((tag) => (
                                                        <span
                                                            key={tag.id}
                                                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs"
                                                            style={{ backgroundColor: tag.color + '20', color: tag.color }}
                                                        >
                                                            {tag.name}
                                                        </span>
                                                    ))}
                                                    {recording.tags.length > 2 && (
                                                        <span className="text-xs text-gray-400">+{recording.tags.length - 2}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Extra opnames */}
                                        {summaryAdditionalRecordings.map((additionalRecording, index) => (
                                            <div key={additionalRecording.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                <div className="flex items-center space-x-3">
                                                    <div className="flex-shrink-0">
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                                                            #{index + 2}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{additionalRecording.name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {additionalRecording.duration ? `${additionalRecording.duration} • ` : ''}{new Date(additionalRecording.createdAt).toLocaleDateString('nl-NL')}
                                                        </p>
                                                    </div>
                                                </div>
                                                {additionalRecording.tags && additionalRecording.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1">
                                                        {additionalRecording.tags.slice(0, 2).map((tag) => (
                                                            <span
                                                                key={tag.id}
                                                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs"
                                                                style={{ backgroundColor: tag.color + '20', color: tag.color }}
                                                            >
                                                                {tag.name}
                                                            </span>
                                                        ))}
                                                        {additionalRecording.tags.length > 2 && (
                                                            <span className="text-xs text-gray-400">+{additionalRecording.tags.length - 2}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Supporting Documents Section */}
                                    {summary.supportingDocuments && summary.supportingDocuments.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                                <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                                                    <svg className="w-4 h-4 mr-2 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    {t('summaryEditor.supportingDocuments.title', { count: summary.supportingDocuments.length })}
                                                </h4>
                                                <div className="space-y-2">
                                                    {summary.supportingDocuments.map((doc) => (
                                                        <div key={doc.id} className="flex items-center justify-between p-2 bg-success/5 rounded border border-success/30">
                                                            <div className="flex items-center space-x-2">
                                                                <div className="flex-shrink-0">
                                                                    <svg className="w-4 h-4 text-success" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                                    </svg>
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                                                                    {doc.description && (
                                                                        <p className="text-xs text-gray-500">{doc.description}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center space-x-2">
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                                                                    {doc.content_type}
                                                                </span>
                                                                {/* Toon welke recording dit document bij hoort */}
                                                                {doc.file_progress_id && (
                                                                    <span className="text-xs text-gray-400">
                                                                        {doc.file_progress_id.toString() === recording.id ? t('summaryEditor.recordings.mainBadge') :
                                                                            summaryAdditionalRecordings.find(r => r.id === doc.file_progress_id.toString())?.name || t('summaryEditor.supportingDocuments.idLabel', { id: doc.file_progress_id })}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Error Notification */}
            {error && (
                <div className="notice error">
                    <p>{error}</p>
                    <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
                        {canRetry && (
                            <button type="button" onClick={retryProcessing}>{t('common.retry')}</button>
                        )}
                        <button type="button" onClick={clearError}>{t('common.close')}</button>
                    </div>
                </div>
            )}

            {/* Parser warning */}
            {(summary.extractionErrors?.length || 0) > 0 && (
                <div className="notice warning">
                    <div>
                        <p style={{ fontWeight: 600, margin: 0 }}>{t('summaryEditor.parserWarning.title')}</p>
                        <p style={{ margin: '4px 0 0' }}>
                            {t('summaryEditor.parserWarning.body', { count: summary.extractionErrors?.length || 0 })}
                        </p>
                    </div>
                </div>
            )}

            {/* Word-style toolbar */}
            {canEdit && (
                <div ref={editorToolbarRef} className={`editor-word-toolbar${showOptionsMenu ? ' is-menu-open' : ''}`}>
                    <div className="toolbar-group">
                        <button
                            type="button"
                            className="toolbar-btn"
                            onClick={performUndo}
                            disabled={isReadOnly || !editor.isEditable || (!editor.can().undo() && !canUndo)}
                            title={t('summaryEditor.toolbar.undo')}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 14L4 9l5-5" />
                                <path d="M4 9h11a5 5 0 0 1 5 5 5 5 0 0 1-5 5H9" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className="toolbar-btn"
                            onClick={performRedo}
                            disabled={isReadOnly || !editor.isEditable || (!editor.can().redo() && !canRedo)}
                            title={t('summaryEditor.toolbar.redo')}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 14l5-5-5-5" />
                                <path d="M20 9H9a5 5 0 0 0-5 5 5 5 0 0 0 5 5h6" />
                            </svg>
                        </button>
                    </div>

                    {history.length > 0 && (
                        <span className="toolbar-history-indicator" style={{ display: 'none' }}>
                            {historyIndex + 1}/{history.length}
                        </span>
                    )}

                    {getPreviousHistoryContent() && !showReview && (
                        <>
                            <div className="toolbar-divider" />
                            <div className="toolbar-group">
                                <button
                                    type="button"
                                    className={`toolbar-btn ${showDiff ? 'active' : ''}`}
                                    onClick={toggleDiffView}
                                    title={t('summaryEditor.toolbar.toggleDiff')}
                                    disabled={isReadOnly || !editor.isEditable}
                                    aria-pressed={showDiff}
                                >
                                    <svg viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h6v4H7V5zm8 8v2a1 1 0 01-1 1H6a1 1 0 01-1-1v-2h8zm-2-1H7v-1h6v1z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                        </>
                    )}

                    {canShowFormattingToolbar && editor && (
                        <>
                            <div className="toolbar-divider" />
                            <div className="toolbar-group">
                                <button
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => editor.chain().focus().toggleBold().run()}
                                    disabled={isReadOnly || !editor.isEditable}
                                    className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
                                    title={t('summaryEditor.toolbar.bold')}
                                >
                                    <strong>B</strong>
                                </button>
                                <button
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => editor.chain().focus().toggleItalic().run()}
                                    disabled={isReadOnly || !editor.isEditable}
                                    className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
                                    title={t('summaryEditor.toolbar.italic')}
                                >
                                    <em>I</em>
                                </button>
                            </div>

                            <div className="toolbar-divider" />
                            <div className="toolbar-group heading-group">
                                <button
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                                    disabled={isReadOnly || !editor.isEditable}
                                    className={`toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
                                    title={t('summaryEditor.toolbar.heading1')}
                                >
                                    H1
                                </button>
                                <button
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                                    disabled={isReadOnly || !editor.isEditable}
                                    className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
                                    title={t('summaryEditor.toolbar.heading2')}
                                >
                                    H2
                                </button>
                                <button
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                                    disabled={isReadOnly || !editor.isEditable}
                                    className={`toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
                                    title={t('summaryEditor.toolbar.heading3')}
                                >
                                    H3
                                </button>
                            </div>

                            <div className="toolbar-divider" />
                            <div className="toolbar-group">
                                <button
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                                    disabled={isReadOnly || !editor.isEditable}
                                    className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
                                    title={t('summaryEditor.toolbar.bulletList')}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                        <circle cx="4" cy="6" r="1.5" fill="currentColor" />
                                        <circle cx="4" cy="12" r="1.5" fill="currentColor" />
                                        <circle cx="4" cy="18" r="1.5" fill="currentColor" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 6h12M8 12h12M8 18h12" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                                    disabled={isReadOnly || !editor.isEditable}
                                    className={`toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
                                    title={t('summaryEditor.toolbar.orderedList')}
                                >
                                    <svg viewBox="0 0 24 24" fill="currentColor">
                                        <text x="2" y="8" fontSize="7" fontWeight="bold">1</text>
                                        <text x="2" y="14" fontSize="7" fontWeight="bold">2</text>
                                        <text x="2" y="20" fontSize="7" fontWeight="bold">3</text>
                                        <line x1="9" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        <line x1="9" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        <line x1="9" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>

                            {/* Tabelbediening: verschijnt alleen wanneer de cursor in een
                                tabel staat, zodat verslagen zonder tabel geen extra knoppen
                                tonen. Kolombreedtes pas je aan door de randen te slepen. */}
                            {editor.isActive('table') && (
                                <>
                                    <div className="toolbar-divider" />
                                    <div className="toolbar-group">
                                        <button
                                            type="button"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => editor.chain().focus().addColumnAfter().run()}
                                            disabled={isReadOnly || !editor.isEditable}
                                            className="toolbar-btn"
                                            title={t('summaryEditor.toolbar.addColumn')}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="4" y="4" width="6" height="16" rx="1.5" />
                                                <line x1="16" y1="9" x2="16" y2="15" />
                                                <line x1="13" y1="12" x2="19" y2="12" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => editor.chain().focus().deleteColumn().run()}
                                            disabled={isReadOnly || !editor.isEditable || !editor.can().deleteColumn()}
                                            className="toolbar-btn"
                                            title={t('summaryEditor.toolbar.deleteColumn')}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="4" y="4" width="6" height="16" rx="1.5" />
                                                <line x1="13" y1="12" x2="19" y2="12" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => editor.chain().focus().addRowAfter().run()}
                                            disabled={isReadOnly || !editor.isEditable}
                                            className="toolbar-btn"
                                            title={t('summaryEditor.toolbar.addRow')}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="4" y="4" width="16" height="6" rx="1.5" />
                                                <line x1="12" y1="14" x2="12" y2="20" />
                                                <line x1="9" y1="17" x2="15" y2="17" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => editor.chain().focus().deleteRow().run()}
                                            disabled={isReadOnly || !editor.isEditable || !editor.can().deleteRow()}
                                            className="toolbar-btn"
                                            title={t('summaryEditor.toolbar.deleteRow')}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="4" y="4" width="16" height="6" rx="1.5" />
                                                <line x1="9" y1="16" x2="15" y2="16" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            onMouseDown={(event) => event.preventDefault()}
                                            onClick={() => editor.chain().focus().deleteTable().run()}
                                            disabled={isReadOnly || !editor.isEditable}
                                            className="toolbar-btn"
                                            title={t('summaryEditor.toolbar.deleteTable')}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M4 7h16" />
                                                <path d="M9 7V5h6v2" />
                                                <path d="M6 7l1 13h10l1-13" />
                                            </svg>
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {canEdit && (
                        <div className="editor-options-menu options-menu-container" style={{ marginLeft: 'auto' }}>
                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowOptionsMenu(!showOptionsMenu);
                                }}
                                title={t('summaryEditor.toolbar.options')}
                                disabled={isSummaryBusy}
                            >
                                <svg viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                </svg>
                            </button>
                            {showOptionsMenu && (
                                <div className="editor-options-popover" onClick={(e) => e.stopPropagation()}>
                                    <button type="button" className="editor-options-item" onClick={handleRegenerateRequest}>
                                        {t('summaryEditor.options.regenerate')}
                                    </button>
                                    <button type="button" className="editor-options-item is-danger" onClick={confirmDelete}>
                                        {t('summaryEditor.options.deleteReport')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {showReview && (
                <SummaryChangesReview
                    status={reviewStatus}
                    description={reviewDescription}
                    active={isStreaming}
                    blocked={isStreaming && reviewBlocked}
                    disabled={isSummaryBusy}
                    canRestore={!!lastAiChangeRef.current && !isReadOnly && !saveQueue.conflict}
                    onNavigate={navigateAiChange}
                    changeCount={reviewGroups.length}
                    selectedChange={reviewGroups.findIndex(group => group.changeIds.includes(review.selected ?? -1))}
                    onKeep={keepAiChanges}
                    onRestore={restoreBeforeAiChanges}
                />
            )}

            {(generation.running || isStreaming) && (
                <div className="editor-streaming-status">
                    <span className="editor-streaming-status__message" role="status" aria-atomic="true">
                        <span className="summary-generation-status-dot" aria-hidden="true" />
                        <span className="editor-streaming-status__label" title={streamingStatus}>{streamingStatus}</span>
                    </span>
                    {isStreaming && !updateVersion && <button type="button" className="stop-streaming" onClick={stopStreaming}
                        title={t('summaryEditor.stop')} aria-label={t('summaryEditor.stop')}>
                        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><rect x="4" y="4" width="8" height="8" rx="1.5" /></svg>
                    </button>}
                </div>
            )}

            {/* Main layout */}
            <div className="editor-main-layout">
                <div className="editor-content">
                    <div style={{ display: showFieldOverlay && canShowFieldOverlay ? 'block' : 'none' }}>
                        <SmartFieldOverlayCanvas
                            content={editorCleanContent}
                            assignments={fieldAssignments}
                            smartFields={overlaySmartFields}
                            onAssignmentsChange={handleFieldAssignmentsChange}
                            minHeight="100%"
                            chromeless
                        />
                    </div>
                    <div style={{ display: showFieldOverlay && canShowFieldOverlay ? 'none' : 'block' }}>
                        <div className="editor-rendered">
                            {generation.blocked && !generation.running && !generation.refreshing && (generation.message || generation.error) && (
                                <div className="summary-generation-error" role="alert">
                                    <p>{generation.message || generation.error}</p>
                                    {generation.error && generation.message && generation.error !== generation.message &&
                                        <details><summary>{t('summaryEditor.generation.errorDetails')}</summary><p>{generation.error}</p></details>}
                                    <div className="summary-generation-error-actions">
                                        {generation.canRetry
                                            ? <button type="button" className="action-button primary" onClick={() => { void startInitialGeneration(true); }}>{t('summaryEditor.generation.retry')}</button>
                                            : <button type="button" className="action-button" onClick={generation.refresh}>{t('summaryEditor.generation.refresh')}</button>}
                                    </div>
                                </div>
                            )}
                            {generation.refreshing && <p className="summary-generation-recovery" role="status">{generation.message}</p>}
                            {!generation.blocked && generation.error && <p className="summary-generation-recovery" role="status">{generation.error}</p>}
                            <EditorContent editor={editor} className="summary-editor-content" />
                        </div>
                    </div>
                </div>

                {/* Floating Action Panel — flex sibling of the content column */}
                {(canShareSummary || canEdit) && (
                    <div className={`summary-floating-panel-wrapper ${isChatOpen ? 'summary-floating-panel-wrapper--hidden' : ''}`}>
                        <div id="summary-editor-floating-action-buttons" className="summary-floating-panel">
                            <div className="summary-floating-panel__header">{t('summaryEditor.floatingPanel.header')}</div>

                            {canEdit && (
                                <>
                                    <button
                                        type="button"
                                        onMouseDown={event => event.preventDefault()}
                                        onClick={() => setShowPromptBar(!showPromptBar)}
                                        disabled={isSummaryBusy}
                                        className={`summary-floating-panel__action ${showPromptBar ? 'is-active' : ''}`}
                                        aria-pressed={showPromptBar}
                                        title={t('summaryEditor.floatingPanel.improveTitle')}
                                    >
                                        <span className="summary-floating-panel__icon">
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                                            </svg>
                                        </span>
                                        <span className="summary-floating-panel__label">{t('summaryEditor.floatingPanel.improveLabel')}</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setIsChatOpen(prev => !prev)}
                                        className={`summary-floating-panel__action ${isChatOpen ? 'is-active' : ''}`}
                                        aria-pressed={isChatOpen}
                                        title={t('summaryEditor.floatingPanel.askTitle')}
                                    >
                                        <span className="summary-floating-panel__icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 7.5c-2 0-3.5 1.3-3.5 3" />
                                                <path d="M12 14.5h0" />
                                                <path d="M5 19v-6a7 7 0 117 7H5z" />
                                            </svg>
                                        </span>
                                        <span className="summary-floating-panel__label">{t('summaryEditor.floatingPanel.askLabel')}</span>
                                    </button>

                                    {canShowFieldOverlay && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (showFieldOverlay) {
                                                    setShowFieldOverlay(false);
                                                    return;
                                                }
                                                openFieldOverlay();
                                            }}
                                            disabled={isSummaryBusy}
                                            className={`summary-floating-panel__action ${showFieldOverlay ? 'is-active' : ''}`}
                                            aria-pressed={showFieldOverlay}
                                            title={t('summaryEditor.floatingPanel.fieldsTitle')}
                                        >
                                            <span className="summary-floating-panel__icon">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="4" y="4" width="16" height="16" rx="2" />
                                                    <path d="M4 10h16" />
                                                    <path d="M10 10v10" />
                                                </svg>
                                            </span>
                                            <span className="summary-floating-panel__label">{t('summaryEditor.floatingPanel.fieldsLabel')}</span>
                                        </button>
                                    )}
                                </>
                            )}

                            {canShareSummary && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (!await flushManualSave()) return;
                                        setShareContent(getCurrentEditorSourceContent());
                                        setShowShareDialog(true);
                                    }}
                                    disabled={isSummaryBusy}
                                    className={`summary-floating-panel__share ${showShareDialog ? 'is-active' : ''}`}
                                    aria-pressed={showShareDialog}
                                    title={t('summaryEditor.floatingPanel.shareTitle')}
                                >
                                    <span className="summary-floating-panel__share-circle">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                            <path d="M7 13l5-5 5 5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M12 8v8" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                    <span className="summary-floating-panel__share-label">{t('summaryEditor.floatingPanel.shareLabel')}</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* AI Prompt Panel — de dock spiegelt de flex-geometrie van
                .editor-main-layout zodat de bar exact met de contentkolom uitlijnt */}
            {canEdit && showPromptBar && (
                <div className="prompt-bar-dock">
                <div className="prompt-bar">
                    <div className="prompt-header">
                        <h3>{t('summaryEditor.promptBar.title')}</h3>
                        <button type="button" className="close-prompt" onClick={() => { setShowPromptBar(false); editor.commands.focus(undefined, { scrollIntoView: false }); }} title={t('common.close')}>
                            ×
                        </button>
                    </div>

                    <p className="prompt-scope">{t(selectedText.trim() ? 'summaryEditor.promptBar.selectionScope' : 'summaryEditor.promptBar.documentScope')}</p>
                    {selectedText && selectedText.trim() && (
                        <div className="prompt-selected-context">
                            <span className="prompt-selected-context__label">{t('summaryEditor.promptBar.selectedContext')}</span>
                            <span className="prompt-selected-context__text">"{selectedText}"</span>
                        </div>
                    )}

                    <div className="prompt-input-group">
                        <textarea
                            className="prompt-input"
                            aria-describedby={promptError ? 'summary-prompt-error' : undefined}
                            value={promptValue}
                            onChange={(e) => setPromptValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    e.preventDefault();
                                    setShowPromptBar(false);
                                    editor.commands.focus(undefined, { scrollIntoView: false });
                                }
                                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) {
                                    e.preventDefault();
                                    if (promptValue.trim()) {
                                        handlePromptSubmit(promptValue);
                                    }
                                }
                            }}
                            onFocus={showSelectionContextHighlight}
                            placeholder={t('summaryEditor.promptBar.placeholder')}
                            rows={2}
                            autoFocus
                            disabled={isSummaryBusy}
                        />
                        <button
                            type="button"
                            className="prompt-submit"
                            onClick={() => handlePromptSubmit(promptValue)}
                            disabled={!promptValue.trim() || isSummaryBusy}
                        >
                            {isStreaming ? (
                                <span className="spinner-small" style={{ borderColor: 'rgba(255,255,255,0.4)', borderTopColor: 'transparent' }} />
                            ) : (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            )}
                        </button>
                    </div>

                    {promptError && <p id="summary-prompt-error" className="prompt-error" role="alert">
                        {promptError} {t('summaryEditor.promptBar.retained')}
                        {saveQueue.conflict && promptError === t('summaryEditor.update.resolveDraftFirst') && <button type="button" className="prompt-error-action" disabled={isSummaryBusy}
                            onClick={() => setDraftReviewRequest(value => value + 1)}>{t('summaryEditor.promptBar.reviewVersions')}</button>}
                    </p>}

                    <div className="prompt-suggestions">
                        {getPromptSuggestions().map((suggestion, index) => (
                            <button
                                key={index}
                                type="button"
                                className="suggestion-chip"
                                onClick={() => {
                                    setPromptValue(suggestion);
                                    handlePromptSubmit(suggestion);
                                }}
                                disabled={isSummaryBusy}
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                </div>
                {/* Spiegel van de floating-panel-kolom */}
                <div className="prompt-bar-dock__spacer" aria-hidden="true" />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="summary-delete-title" aria-busy={isDeleting}>
                        <div className="modal-header">
                            <svg className="modal-icon danger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <h3 id="summary-delete-title">{t('summaryEditor.deleteModal.title')}</h3>
                        </div>
                        <p className="modal-message">
                            {t('summaryEditor.deleteModal.message')}
                        </p>
                        {deleteError && <p className="prompt-error" role="alert">{deleteError}</p>}
                        <div className="modal-actions">
                            <button type="button" className="action-button ghost" disabled={isDeleting} onClick={() => setShowDeleteConfirm(false)}>
                                {t('common.cancel')}
                            </button>
                            <button type="button" className="action-button danger" disabled={isDeleting} onClick={handleDeleteSummary}>
                                {t(isDeleting ? 'summaryEditor.deleteModal.deleting' : 'common.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Dialog */}
            <ShareDialog
                isOpen={canShareSummary && showShareDialog}
                onClose={() => setShowShareDialog(false)}
                summaryContent={shareContent || summarySourceContent}
                summaryTitle={summary.title}
                recordingName={recording.name}
                fileId={summary.fileId}
                meetingType={summary.meetingType}
                templateUuid={summary.templateUuid}
                onBeforeExport={flushManualSave}
            />

            <ChatOverlay
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                fileId={summary.fileId}
                meetingType={summary.meetingType}
                templateUuid={summary.templateUuid}
                messages={chatMessages}
                onMessagesChange={setChatMessages}
                onAddToSummary={canEdit ? handleChatAddToSummary : undefined}
                canAddToSummary={Boolean(canEdit)}
            />
        </div >
    );
};

const ScopedSummaryEditor: React.FC<SummaryEditorProps> = props => {
    const { user, getDomain } = useAuth();
    return <SummaryEditor key={JSON.stringify([getDomain(), user?.id, props.summary.fileId, props.summary.meetingType, props.canEdit])} {...props} />;
};
export default ScopedSummaryEditor;
